import { IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, Observable, combineLatest, first, firstValueFrom, of, take, takeUntil } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { AssigneeActions } from "@gtmhub/assignees/redux/assignee-actions";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { AccountType, isGtmhubAccountId } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { IEmployee } from "@gtmhub/employees/models/models";
import { isGoalOwnedByTeamOfEmployee } from "@gtmhub/employees/util";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { PolicyService } from "@gtmhub/goals/services/policy.service";
import { groupGoalsBySession } from "@gtmhub/links";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { ItemType } from "@gtmhub/okrs/alignment-tree/models";
import { CloneOkrsModal } from "@gtmhub/okrs/components/clone-okrs/clone-okrs.component";
import { ResourceType } from "@gtmhub/okrs/components/clone-okrs/clone-or-move.types";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { CascadingMetricFormModal } from "@gtmhub/okrs/metrics/components/cascading-metric-form/cascading-metric-form.component";
import { DisableMetricSyncModal } from "@gtmhub/okrs/metrics/components/disable-metric-sync/disable-metric-sync.modal.component";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { formatAttainmentProgressValue } from "@gtmhub/okrs/metrics/utils";
import { Ng1ConfirmGoalDeleteService } from "@gtmhub/okrs/services/ng1-confirm-goal-delete.service";
import { OnboardingService } from "@gtmhub/onboarding";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { isCurrentUserOwner } from "@gtmhub/sessions/utils/granular-permission.util";
import { IDndDroppedParams, IDndInsertedParams, IOnDndDroppedCbResponse } from "@gtmhub/shared/components/dnd-list/dnd-list.component";
import { IParentSelectorEntry } from "@gtmhub/shared/components/parent-okr-selector/models";
import { copyToClipboard } from "@gtmhub/shared/dom";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { getCopyURLNotificationEl } from "@gtmhub/shared/utils/dom-utils";
import { INgRedux } from "@gtmhub/state-management";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { ITag } from "@gtmhub/tags";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { IReduxTeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { toKeyMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { ICollection } from "@webapp/core/core.models";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { areCustomFieldsChanged } from "@webapp/custom-fields/utils";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { IDesignScoreForUpdate, IGoalPoliciesMap, IGoalPolicy } from "@webapp/okrs/goals/models/goal-design-score.models";
import { AttainmentTypeString } from "@webapp/okrs/goals/models/goal.models";
import { Metric, MetricMilestoneEventType } from "@webapp/okrs/metrics/models/metric.models";
import { UpdateMetricModalService } from "@webapp/okrs/metrics/services/update-metric-modal.service";
import { getProgressDescription } from "@webapp/okrs/utils/utils";
import {
  QuantivePlusObjectivesEffectiveness,
  QuantivePlusObjectivesEffectivenessPayload,
  SuggestedEffectivenessTitle,
} from "@webapp/platform-intelligence/expandable-suggestions/shared/models/quantive-plus-effectiveness.models";
import {
  QuantivePlusObjectivesExplanationPayload,
  QunativePlusExplanation,
} from "@webapp/platform-intelligence/expandable-suggestions/shared/models/quantive-plus-explanation.models";
import { QuantivePlusEffectivenessFacade } from "@webapp/platform-intelligence/expandable-suggestions/shared/services/quantive-plus-effectiveness/quantive-plus-effectiveness-facade.service";
import { QuantivePlusExplanationFacade } from "@webapp/platform-intelligence/expandable-suggestions/shared/services/quantive-plus-explanation/quantive-plus-explanation-facade.service";
import { generateQuantivePlusObjectivesEffectivenessPayload } from "@webapp/platform-intelligence/expandable-suggestions/shared/utils/quantive-plus-effectiveness.utils";
import {
  SuggestionDrawerSectionType,
  SuggestionDrawerSuggestionGroupType,
  SuggestionDrawerTitleSuggestionParams,
} from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/models/suggestion-drawer.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import { GuidedOKRsComponent, GuidedOKRsModalData } from "@webapp/platform-intelligence/guided-okrs/guided-okrs.component";
import {
  IQuantivePlusAcceptedMetric,
  IQuantivePlusMetricsAndTasksSuggestionStatus,
  IQuantivePlusRefreshedArgs,
  IQuantivePlusSuggestionStatus,
  IQuantivePlusTag,
  IQuantivePlusTask,
  maxKrsSuggestions,
  minTasksCountNotToFetchSuggestions,
} from "@webapp/platform-intelligence/quantive-plus/models";
import { QuantivePlusAnotherSuggestionService } from "@webapp/platform-intelligence/quantive-plus/services/quantive-plus-another-suggestion.service";
import {
  generateAssignees,
  generateDummyMetricSuggestion,
  generateDummyTaskSuggestion,
  generateMetricsSuggestionsStatus,
  generateQuantivePlusObjectiveDescriptionSuggestionPayload,
  generateQuantivePlusObjectiveTagsSuggestionPayload,
  generateQuantivePlusSuggestionsPayload,
  generateTasksSuggestionsStatus,
  getExistingKrsFromGoal,
} from "@webapp/platform-intelligence/quantive-plus/utils/utils";
import { IPIFeedbackSuggestion } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/models";
import { PIImproveSuggestionContext, PIImprovementSuggestionType } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/models";
import { PiImprovementFacade } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/pi-improvement-facade.service";
import {
  generateImprovementPayload,
  generateTagImprovementPayload,
  tagsImprovementSplitOrJoinSuffix,
} from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/utils";
import {
  PIStateProcessorInstanceSubType,
  PiStateProcessorInstance,
} from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.models";
import { PiStateProcessorService } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.service";
import { removeAllPersistedSuggestionsFeedback } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/utils/utils";
import { IQuantivePlusMetric, IQuantivePlusObjectiveDescription } from "@webapp/platform-intelligence/shared/models";
import { PiCardBorderStyle, PiTextCardFontColor } from "@webapp/platform-intelligence/shared/models/pi-card.models";
import { PIKrSuggestionPayload, PIObjectiveTagsSuggestion } from "@webapp/platform-intelligence/shared/models/quantive-plus-suggestions.models";
import { PIObjectiveDescriptionSuggestion } from "@webapp/platform-intelligence/shared/models/strategic-guided-okr.models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedGoalStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-goal-state.service";
import { QuantivePlusSuggestionsFacade } from "@webapp/platform-intelligence/shared/services/quantive-plus-suggestions/quantive-plus-suggestions-facade.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { ObjectiveFlowName, PiToggleButton, PiTrackingEventsEnum, PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { PlanningSessionStatus, Session, UserAction } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import { TrackingEvents } from "@webapp/shared/utils/tracking";
import { Task } from "@webapp/tasks/models/tasks.models";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { CloneMoveOkrModalService } from "../../../../webapp/src/app/okrs/components/clone-move-okrs/clone-move-okr-modal.service";
import { IGoalMethodologySettingsUI, OkrMethodologyFacade } from "../components/facades/okr-methodology.facade";
import {
  ContributorType,
  IAllPoliciesMap,
  IGoal,
  IGoalChildReorderPayload,
  IGoalChildReorderResponse,
  IGoalParent,
  IGoalSupport,
  IWorkflowActions,
  OkrMethodologySettings,
} from "../models";
import { GoalModalsService } from "../services/goal-modals.service";
import { GoalService, UpdateGoalAdditionalPayloadData } from "../services/goal.service";
import { getParentState } from "../utils";

interface IGoalStateParams {
  id: string;
  openingState?: "edit";
  sharedGoal?: string;
}

interface IGoalRedux {
  allSessions: Session[];
  assigneesIdMap: Record<string, Assignee>;
  employee: IEmployee;
  teams: IReduxTeam[];
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

interface IPermissionsMap {
  canAddMetric: boolean;
  canCloneGoal: boolean;
  canMoveGoal: boolean;
  canAddChildGoal: boolean;
  canSubmitToMarketplace: boolean;
  canDeleteGoal: boolean;
  canSeeHistory: boolean;
  areSuggestionsEnabled(): Promise<boolean>;
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState & ICurrentEmployeeStoreState & ITeamsStoreState): IGoalRedux => {
  return {
    allSessions: state.sessions.items,
    assigneesIdMap: state.assignees.map,
    employee: state.currentEmployee.info,
    teams: state.teams.items,
    reduxItemsFetched: state.sessions.isFetched && state.assignees.isFetched && state.currentEmployee.isFetched && state.teams.isFetched,
    reduxItemsError: state.sessions.error || state.assignees.error || state.currentEmployee.error || state.teams.error,
  };
};

export interface IGoalRootScopeService extends IGtmhubRootScopeService {
  planningSession: Session;
}

type DisallowedMetricCreationReason = "abandoned" | "session-in-progress" | "approved" | "metrics-limit-reached";

export interface IGoalScope extends IModalScope, IExtendedRootScopeService, IGoalRedux {
  isTagsFocused: boolean;
  isDescriptionFocused: boolean;
  onDescriptionFocus(): void;
  trackContextualSuggestionsToggle(opts: { state: boolean; subEntity: PIStateProcessorInstanceSubType }): void;
  isAtLeastOneKrHidden(): boolean;

  setDescriptionSuggestionVisibility(): void;
  isDescriptionSuggestionVisible: boolean;

  setTagsSuggestionVisibility(): void;
  isTagsSuggestionVisible: boolean;

  setKeyResultsSuggestionVisibility(): void;
  isKeyResultsSuggestionVisible: boolean;

  piContextualSuggestionsEnabled: boolean;
  onQuantivePlusMetricRejected(index: number): void;
  onQuantivePlusTaskRejected(index: number): void;

  tagsSuggestion: IPIFeedbackSuggestion<string[]>;
  favoritesManager: FavoritesManager;
  allowedActions: UserAction[];
  sessionAllowedActions: UserAction[];
  error: string;
  customPrivateError: {
    type: string;
    title: string;
  };
  goal: IGoal & IGoalSupport;
  parentItem: IGoal | Metric;
  goalPoliciesMap: IGoalPoliciesMap;
  allPoliciesMap: IAllPoliciesMap;
  indicators: {
    delete?: IIndicator;
    getGoal?: IIndicator;
    loadingGoalProgress?: boolean;
  };
  suggestionsStatus: {
    description?: IQuantivePlusSuggestionStatus;
    tags?: IQuantivePlusSuggestionStatus;
    metricsAndTasks?: IQuantivePlusMetricsAndTasksSuggestionStatus;
    metrics?: IQuantivePlusSuggestionStatus;
    tasks?: IQuantivePlusSuggestionStatus;
  };
  isTrialAccount: boolean;
  isAllowedToSubmitToMarketplace: boolean;
  planningSession: Session;
  subGoals: IGoal[];
  goalChildren: ContributorType[];
  allowEditGoalFields: boolean;
  hasTeamInOwners: boolean;
  ui: {
    disabled: boolean;
  };
  suggestions: {
    keyresults: IQuantivePlusMetric[];
    tasks: IQuantivePlusTask[];
    sessionId: string;
  };
  workflowActions: IWorkflowActions;
  hasWorkflow: boolean;
  focusOnNameField: boolean;
  shouldSeeOnboardingExperiment: boolean;
  canChangeClosedStatus: boolean;
  employee: IEmployee;
  okrForParentSelector: Partial<IParentSelectorEntry>;
  sessionsForParentSelector: string[];
  methodologySettings: IGoalMethodologySettingsUI & { isProgressAggregationDisabled: boolean; metricsPerGoalLimit: number };
  sharedOkrsMethodologySettings: OkrMethodologySettings;
  metricsLimitReached: boolean;
  parentState: string;
  canAccessSession: boolean;
  isOkrAlignmentMandatory: boolean;
  isSuggestingDescriptionToOKRsEnabled: boolean;
  isSuggestingKRsToOKRsEnabled: boolean;
  isSuggestingTasksToOKRsEnabled: boolean;
  isSuggestingTagsToOKRsEnabled: boolean;
  isGuidedOKRsCreationEnabled: boolean;
  isNewCloneAndMoveModalEnabled: boolean;
  peopleSelectorRequest: PeopleSelectorRequest;
  progressDescription: string;
  quantivePlusCardBorderStyle: PiCardBorderStyle;
  quantivePlusTextCardFontColor: PiTextCardFontColor;
  descriptionSuggested: string;
  descriptionSuggestedV2: {
    id: string;
    text: string;
  };
  flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView;
  createMetricFlowName: PiTrackingFlowNameEnum.CreateMetricFormobjectiveDropdown;
  effectivenessFlowId: string;
  tagsSuggestedV2: string[];
  tagsSuggested: IQuantivePlusTag[];
  shouldFocusDescriptionTextEditor: boolean;
  shouldFocusTagsInput: boolean;
  shouldOpenTagsInput: boolean;
  krsSuggestionsExpected: number;
  shouldDisplaySuggestionDrawer: boolean;
  drawerExplanationSection: SuggestionDrawerSectionType<{ flowId: string; explanation: QunativePlusExplanation; refreshExplanation(): void }>;
  drawerEffectivenessSection: SuggestionDrawerSectionType<{ effectiveness: QuantivePlusObjectivesEffectiveness; refreshEffectiveness(): void }>;
  drawerTitleSection: SuggestionDrawerSectionType<{ title: SuggestionDrawerTitleSuggestionParams } & SuggestionDrawerSuggestionGroupType>;
  drawerStatePropagator: SuggestionDrawerIndicatorStatePropagator;
  isSuggestionStateIndicatorEnabled: boolean;
  isSuggestionDrawerOverviewTabEnabled: boolean;
  isSuggestionDrawerEffectivenessTabEnabled: boolean;
  relatedItemsFeatureEnabled: boolean;
  isTagsModuleEnabled: boolean;
  descriptionInstance: PiStateProcessorInstance;
  metricsInstance: PiStateProcessorInstance;
  tagsInstance: PiStateProcessorInstance;
  tasksInstance: PiStateProcessorInstance;
  initCustomFields: CfMap;
  dropdownMenuItems: DropdownMenuItem[];

  closeGoalDetailsModalOnClick($event): void;
  closeGoalDetailsModalOnEnter($event): void;
  updateGoalParent(parent: IGoalParent): void;
  clone(): void;
  openCloneModal(): void;
  openMoveModal(): void;
  confirmDeleteGoal(): void;
  confirmDeleteMetric(metricId: string | { itemId: string }): void;
  updateField(goal: IGoal, fields: string[], additionalPayloadData: UpdateGoalAdditionalPayloadData): IPromise<void>;
  updateName(value: string): void;
  updateCustomFields(customFields: CfMap): void;
  updateDescription(): void;
  switchProgressCalculationCriteria(newCriteria: string): void;
  onTextareaClick(event): void;
  openCloneMetricModal(metric: Metric): void;
  openMoveMetricModal(metric: Metric): void;
  openUpdateMetricModal(metricId: string): void;
  selectAssignee(assignee: Assignee): void;
  selectMultipleOwners(ids: string[]): void;
  changeSession(sessionId: string): void;
  addChildObjective(itemId: string, itemType: ItemType, itemSessionId: string): void;
  openDisableSyncModal(metricId: string): void;
  canUpdateMetric(metric: Metric): boolean;
  metricsContributeToGoalProgress(): boolean;
  goalUnlockedAndSessionOpen(): boolean;
  goalUnlockedUnabandonedSessionOpen(): boolean;
  reorderGoalChild(params: IDndDroppedParams<ContributorType>): IPromise<IOnDndDroppedCbResponse<IGoalChildReorderResponse>>;
  cdkReorderGoalChild(params: IDndDroppedParams<ContributorType>): IPromise<IOnDndDroppedCbResponse<IGoalChildReorderResponse>>;
  onReorderSuccess(params: IDndDroppedParams<ContributorType>): void;
  handleOpenCascadingForm(metric: Metric, goalName: string): void;
  handleOpenCascadingFormFromContributor(input: { metric: Metric; goalName: string }): void;
  getInfoTooltipReason(): string;
  copyItemUrlToClipboard(itemId: string, type: "metric" | "goal"): void;
  copyItemUrlFromContributors(input: { itemId: string; type: "metric" | "goal" }): void;
  getShareableUrl(itemId: string, type: "metric" | "goal"): void;
  isFieldPartOfWorkflow(fieldName: string): boolean;
  getNavItem(): INavItem;
  goalIsFavorite(): boolean;

  getQuantivePlusSuggestions(): void;
  onQuantivePlusMetricCreated(args: IQuantivePlusAcceptedMetric): void;
  goBackToLastAvailableMetricSuggestion(args: { suggestionIndex: number }): void;
  getQuantivePlusDescriptionSuggestion(args?: { isRefresh: boolean }): void;
  onDescriptionSuggestionRemoved(args?: { isError: boolean }): void;
  goBackToLastAvailableDescriptionSuggestion(): void;
  onDescriptionSuggestionAdded(): void;
  getQuantivePlusTagsSuggestion(args?: { isRefresh: boolean }): void;
  goBackToLastAvailableTagsSuggestion(): void;
  onTagsSuggestionRemoved(args?: { isError: boolean }): void;
  onTagsSuggestionAdded(): void;
  handleTagsInputBlur(): void;
  onTagsInputOpenChange(isOpen: boolean): void;
  shouldSuggestQuantivePlusMetrics(): boolean;
  changeQuantivePlusSuggestion(args: IQuantivePlusRefreshedArgs): void;
  onGoalTaskAdded(suggestionIndex?: number, currentGoalTasksCount?: number, flowName?: ObjectiveFlowName): void;
  onGoalTaskAddedMigrated(event: { suggestionIndex?: number; currentGoalTasksCount?: number; flowName?: ObjectiveFlowName }): void;
  onGoalTaskRemoved(currentGoalTasksCount: number): void;
  onGoalTaskRemovedMigrated(event: { currentGoalTasksCount: number }): void;

  openQuantivePlusWizard(goal: IGoal): void;
  formatAttainmentProgressValue(progress: number): number;
  onSuggestionDrawerVisibilityChange(isVisible: boolean): void;
  isCustomPermissionAllowed(permission: UserAction): boolean;
  handleClickOnHiddenOverflowArea($event): void;
  filterBySessionId(customFields: ICustomField[]): ICustomField[];
  isFieldDisabled(fieldName: string): boolean;
  openGoalInAlignmentView($event): void;
  generatePermissionsMap(): object;
}

export class GoalCtrl implements IStateInit {
  private okrEvents: OkrEvents;
  private designScoreForUpdate: IDesignScoreForUpdate;
  private goalDataLoading: boolean;
  private metricId: string;
  private isRestrictedParentSession = false;
  private persistedGoalFields: {
    description: string;
    customFields: CfMap;
  } = { description: "", customFields: {} };
  private isTagsInputOpen: boolean;
  private currentDescriptionSuggestions: IQuantivePlusObjectiveDescription[];
  private descriptionSuggestion: PIObjectiveDescriptionSuggestion;
  private tagsSuggestion: PIObjectiveTagsSuggestion;
  private quantiveTagsId: string;

  public favoritesManager: FavoritesManager;

  private quantiveDescriptionFlowId: string;
  private quantiveOKRsFlowId: string;
  private destroy$: Observable<unknown>;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "GoalService",
    "MetricService",
    "AccountResolverService",
    "PlanningSessionsActions",
    "AssigneeActions",
    "PermissionsService",
    "$q",
    "$timeout",
    "UIErrorHandlingService",
    "PolicyService",
    "GoalModalsService",
    "$ngRedux",
    "ProgressColoring",
    "OnboardingService",
    "CloneOkrsModal",
    "DisableMetricSyncModal",
    "CurrentEmployeeActions",
    "FeatureTogglesFacade",
    "CascadingMetricFormModal",
    "OkrMethodologyFacade",
    "FavoritesManagerFactory",
    "AccountService",
    "CurrentUserService",
    "TeamsActions",
    "QuantivePlusSuggestionsFacade",
    "QuantivePlusAnotherSuggestionService",
    "TaskService",
    "Ng1UiModalService",
    "Ng1ConfirmDeleteService",
    "AnalyticsService",
    "PiFeatureVisibilityFacade",
    "QuantivePlusExplanationFacade",
    "QuantivePlusEffectivenessFacade",
    "PiSuggestionSharedGoalStateService",
    "PiStateProcessorService",
    "PiImprovementFacade",
    "UpdateMetricModalService",
    "CloneMoveOkrModalService",
    "Ng1ConfirmGoalDeleteService",
  ];

  constructor(
    private $rootScope: IGoalRootScopeService & ITraceRootScopeService,
    private $scope: IGoalScope,
    private $state: StateService,
    private $stateParams: IGoalStateParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private accountResolverService: AccountResolverService,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private permissionsService: PermissionsService,
    private $q: IQService,
    private $timeout: ITimeoutService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private policyService: PolicyService,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private progressColoring: ProgressColoring,
    private onboardingService: OnboardingService,
    private cloneOkrsModal: CloneOkrsModal,
    private disableMetricSyncModal: DisableMetricSyncModal,
    private currentEmployeeActions: CurrentEmployeeActions,
    private featureToggleService: FeatureTogglesFacade,
    private cascadingMetricFormModal: CascadingMetricFormModal,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private currentUserService: CurrentUserService,
    private teamsActions: TeamsActions,
    private quantivePlusSuggestionsFacade: QuantivePlusSuggestionsFacade,
    private quantivePlusAnotherSuggestionService: QuantivePlusAnotherSuggestionService,
    private taskService: TaskService,
    private modalService: UiModalService,
    private confirmDeleteService: Ng1ConfirmDeleteService,
    private analyticsService: AnalyticsService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private quantivePlusExplanationFacade: QuantivePlusExplanationFacade,
    private quantivePlusEffectivenessFacade: QuantivePlusEffectivenessFacade,
    private piSuggestionSharedGoalStateService: PiSuggestionSharedGoalStateService,
    private piStateProcessorService: PiStateProcessorService,
    private piImprovementFacade: PiImprovementFacade,
    private updateMetricModalService: UpdateMetricModalService,
    private cloneMoveModalService: CloneMoveOkrModalService,
    private confirmGoalDeleteService: Ng1ConfirmGoalDeleteService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.favoritesManager = this.favoritesManagerFactory(["goal"]);
    this.$scope.handleClickOnHiddenOverflowArea = this.handleClickOnHiddenOverflowArea;
    this.destroy$ = new Ng1DestroyService(this.$scope);

    this.$scope.setDescriptionSuggestionVisibility = this.setDescriptionSuggestionVisibility;
    this.$scope.setTagsSuggestionVisibility = this.setTagsSuggestionVisibility;
    this.$scope.setKeyResultsSuggestionVisibility = this.setKeyResultsSuggestionVisibility;
    this.$scope.isAtLeastOneKrHidden = this.isAtLeastOneKrHidden;
    this.$scope.trackContextualSuggestionsToggle = this.trackContextualSuggestionsToggle;

    this.$scope.onQuantivePlusMetricRejected = this.onQuantivePlusMetricRejected;
    this.$scope.onQuantivePlusTaskRejected = this.onQuantivePlusTaskRejected;
    this.$scope.onDescriptionFocus = this.onDescriptionFocus;

    this.initContextualSuggestions();
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.addReturnStateForSharedUrl();
    this.$scope.parentState = getParentState(this.$state.current.name);
    this.$scope.formatAttainmentProgressValue = formatAttainmentProgressValue;
    this.$scope.flowName = PiTrackingFlowNameEnum.ObjectiveDetailsView;
    this.$scope.createMetricFlowName = PiTrackingFlowNameEnum.CreateMetricFormobjectiveDropdown;

    this.$scope.focusOnNameField = this.$stateParams.openingState === "edit";

    this.$rootScope.planningSession = {};
    this.$scope.indicators = {
      getGoal: { progress: true },
    };
    this.$scope.ui = {
      disabled: true,
    };
    this.$scope.isTrialAccount = this.accountResolverService.getAccountData().type === AccountType.TrialAccount;
    this.$scope.allPoliciesMap = {};
    this.$scope.goalPoliciesMap = {};
    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.dropdownMenuItems = [];
    this.$scope.tagsSuggested = [];
    this.$scope.suggestionsStatus = {};
    this.$scope.quantivePlusCardBorderStyle = "dashed";
    this.$scope.quantivePlusTextCardFontColor = "medium";

    this.setDefaultSharedOkrsMethodologySettings();

    this.$scope.shouldDisplaySuggestionDrawer = false;
    this.setSuggestionDrawerInitialValues();

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.$scope.confirmDeleteGoal = this.confirmDeleteGoal;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.updateField = this.updateField;
    this.$scope.updateName = this.updateName;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.updateDescription = this.updateDescription;
    this.$scope.switchProgressCalculationCriteria = this.switchProgressCalculationCriteria;
    this.$scope.onTextareaClick = this.onTextareaClick;
    this.$scope.openCloneModal = this.openCloneModal;
    this.$scope.openMoveModal = this.openMoveModal;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.openMoveMetricModal = this.openMoveMetricModal;
    this.$scope.openUpdateMetricModal = this.openUpdateMetricModal;
    this.$scope.changeSession = this.changeSession;
    this.$scope.addChildObjective = this.addChildObjective;
    this.$scope.updateGoalParent = this.updateGoalParent;
    this.$scope.reorderGoalChild = this.reorderGoalChild;
    this.$scope.cdkReorderGoalChild = this.cdkReorderGoalChild;
    this.$scope.onReorderSuccess = this.onReorderSuccess;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.closeGoalDetailsModalOnClick = this.closeGoalDetailsModalOnClick;
    this.$scope.closeGoalDetailsModalOnEnter = this.closeGoalDetailsModalOnEnter;
    this.$scope.openDisableSyncModal = this.openDisableSyncModal;
    this.$scope.canUpdateMetric = this.canUpdateMetric;
    this.$scope.metricsContributeToGoalProgress = this.metricsContributeToGoalProgress;
    this.$scope.goalUnlockedAndSessionOpen = this.goalUnlockedAndSessionOpen;
    this.$scope.goalUnlockedUnabandonedSessionOpen = this.goalUnlockedUnabandonedSessionOpen;
    this.$scope.copyItemUrlToClipboard = this.copyItemUrlToClipboard;
    this.$scope.copyItemUrlFromContributors = this.copyItemUrlFromContributors;
    this.$scope.getShareableUrl = this.getShareableUrl;
    this.$scope.handleOpenCascadingForm = (metric, goalName) => this.cascadingMetricFormModal.handleOpenCascadingForm(metric, goalName);
    this.$scope.handleOpenCascadingFormFromContributor = (input: { metric; goalName }) => this.cascadingMetricFormModal.handleOpenCascadingFormFromContributor(input);
    this.$scope.getInfoTooltipReason = this.getInfoTooltipReason;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.goalIsFavorite = this.goalIsFavorite;

    this.$scope.getQuantivePlusSuggestions = this.getQuantivePlusSuggestions;
    this.$scope.onQuantivePlusMetricCreated = this.onQuantivePlusMetricCreated;
    this.$scope.goBackToLastAvailableMetricSuggestion = this.goBackToLastAvailableMetricSuggestion;
    this.$scope.getQuantivePlusDescriptionSuggestion = this.getQuantivePlusDescriptionSuggestion;
    this.$scope.onDescriptionSuggestionRemoved = this.onDescriptionSuggestionRemoved;
    this.$scope.onDescriptionSuggestionAdded = this.onDescriptionSuggestionAdded;
    this.$scope.getQuantivePlusTagsSuggestion = this.getQuantivePlusTagsSuggestion;
    this.$scope.goBackToLastAvailableDescriptionSuggestion = this.goBackToLastAvailableDescriptionSuggestion;
    this.$scope.onTagsSuggestionRemoved = this.onTagsSuggestionRemoved;
    this.$scope.onTagsSuggestionAdded = this.onTagsSuggestionAdded;
    this.$scope.handleTagsInputBlur = this.handleTagsInputBlur;
    this.$scope.goBackToLastAvailableTagsSuggestion = this.goBackToLastAvailableTagsSuggestion;
    this.$scope.onTagsInputOpenChange = this.onTagsInputOpenChange;
    this.$scope.shouldSuggestQuantivePlusMetrics = this.shouldSuggestQuantivePlusMetrics;
    this.$scope.changeQuantivePlusSuggestion = this.changeQuantivePlusSuggestion;
    this.$scope.onGoalTaskAdded = this.onGoalTaskAdded;
    this.$scope.onGoalTaskAddedMigrated = this.onGoalTaskAddedMigrated;
    this.$scope.onGoalTaskRemoved = this.onGoalTaskRemoved;
    this.$scope.onGoalTaskRemovedMigrated = this.onGoalTaskRemovedMigrated;
    this.$scope.openGoalInAlignmentView = this.openGoalInAlignmentView;

    this.$scope.openQuantivePlusWizard = this.openQuantivePlusWizard;
    this.$scope.onSuggestionDrawerVisibilityChange = this.changeSuggestionDrawerVisibility;
    this.$scope.isCustomPermissionAllowed = this.isCustomPermissionAllowed;

    this.$scope.filterBySessionId = this.filterBySessionId;
    this.$scope.isFieldDisabled = this.isFieldDisabled;

    this.$scope.$on("updateGoalDesignScore", (ev, designScoreForUpdate: IDesignScoreForUpdate) => {
      if (this.goalDataLoading) {
        this.designScoreForUpdate = designScoreForUpdate;
        return;
      }

      this.updateGoalDesignScore(designScoreForUpdate);
    });
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.$scope.$on("$destroy", () => {
      unsubscribe();
    });

    this.favoritesManager.initFavorites();

    return this.loadData().then(() => {
      this.setPIInstances();
      this.subscribeToDescriptionImprovement();
      this.subscribeToDescriptionSubject();

      this.subscribeToTagsSubject();
      this.subscribeToTagsImprovement();

      this.setSuggestionListeners();
      this.loadDropdownSuggestions();
      this.loadSuggestionDrawerEffectiveness({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren });
      this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));
    });
  };

  private initContextualSuggestions = (): void => {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.PiContextualSuggestions)
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isDescriptionSuggestionVisible = true;
          this.$scope.isTagsSuggestionVisible = true;
          this.$scope.isKeyResultsSuggestionVisible = true;

          this.$scope.piContextualSuggestionsEnabled = enabled;

          // we make them hidden if feature is enabled, otherwise this is working in contrast with the pi-feedback-card
          // TODO: iliya - remove this feature check when we remove the isPiFeedbackCardEnabled checks
          if (enabled) {
            this.$scope.isDescriptionSuggestionVisible = false;
            this.$scope.isTagsSuggestionVisible = false;
            this.$scope.isKeyResultsSuggestionVisible = false;
          }
        },
      });
  };

  private trackContextualSuggestionsToggle = (opts: { state: boolean; subEntity: PIStateProcessorInstanceSubType }): void => {
    this.analyticsService.track(TrackingEvents.ToggleSet, {
      name: PiToggleButton,
      state: opts.state,
      entity: "goal",
      subEntity: opts.subEntity,
    });
  };

  private isAtLeastOneKrHidden = (): boolean => {
    return this.$scope.suggestions?.keyresults?.some((kr) => kr.isHidden);
  };

  private setDescriptionSuggestionVisibility = (): void => {
    this.$scope.isDescriptionSuggestionVisible = !this.$scope.isDescriptionSuggestionVisible;

    this.trackContextualSuggestionsToggle({ state: this.$scope.isDescriptionSuggestionVisible, subEntity: "description" });
  };

  private setTagsSuggestionVisibility = (): void => {
    this.$scope.isTagsSuggestionVisible = !this.$scope.isTagsSuggestionVisible;

    this.trackContextualSuggestionsToggle({ state: this.$scope.isTagsSuggestionVisible, subEntity: "tags" });
  };

  private setKeyResultsSuggestionVisibility = (): void => {
    if (!this.$scope.isAtLeastOneKrHidden()) {
      this.$scope.isKeyResultsSuggestionVisible = !this.$scope.isKeyResultsSuggestionVisible;

      this.trackContextualSuggestionsToggle({ state: this.$scope.isKeyResultsSuggestionVisible, subEntity: "metrics" });
      return;
    }

    this.$scope.suggestions.keyresults.forEach((kr) => (kr.isHidden = false));
  };

  private loadDropdownSuggestions(): void {
    if (this.$scope.goal) {
      const permissionsMap = this.generatePermissionsMap();
      this.$scope.dropdownMenuItems = this.createDropdownMenuItems(permissionsMap);
    }
  }

  private generatePermissionsMap(): IPermissionsMap {
    const canDeleteGoal =
      !this.$scope.goal.locked &&
      (this.$scope.planningSession.status === "open" || this.$scope.planningSession.status == null) &&
      this.$scope.allowedActions.includes("delete");
    const canSeeHistory = this.$rootScope.featureAvailable("moments");
    const canAddMetric =
      !this.$scope.metricsLimitReached && this.metricsContributeToGoalProgress && this.isCustomPermissionAllowed("create") && this.goalUnlockedUnabandonedSessionOpen();
    const canCloneGoal = this.$scope.allowedActions.includes("create");
    const canMoveGoal = this.$scope.allowedActions.includes("update") && this.goalUnlockedAndSessionOpen();
    const canAddChildGoal = this.goalUnlockedAndSessionOpen() && this.$scope.sessionAllowedActions.includes("create");
    const areSuggestionsEnabled = async () => {
      const isFeatureEnabled = await firstValueFrom(this.piFeatureVisibilityFacade.isGuidedOKRsCreationVisible$());
      return isFeatureEnabled && this.goalUnlockedAndSessionOpen() && this.isCustomPermissionAllowed("create");
    };
    const canSubmitToMarketplace = !this.$scope.isTrialAccount && this.$scope.isAllowedToSubmitToMarketplace && this.isCustomPermissionAllowed("create");
    return {
      canDeleteGoal,
      canSeeHistory,
      canAddMetric,
      canCloneGoal,
      canMoveGoal,
      canAddChildGoal,
      areSuggestionsEnabled,
      canSubmitToMarketplace,
    };
  }

  private createDropdownMenuItems(permissionsMap: IPermissionsMap): DropdownMenuItem[] {
    const menuItemBuilder = new DropdownMenuItemBuilder();
    const dropdownOptions: DropdownMenuItem[] = [];

    if (permissionsMap.canAddMetric) {
      const addMetricItem = this.buildAddMetricItem(menuItemBuilder);
      dropdownOptions.push(addMetricItem);
    }

    if (permissionsMap.canCloneGoal) {
      const cloneGoalItem = this.buildCloneGoalItem(menuItemBuilder);
      dropdownOptions.push(cloneGoalItem);
    }

    if (permissionsMap.canMoveGoal) {
      const moveItem = this.buildMoveItem(menuItemBuilder);
      dropdownOptions.push(moveItem);
    }

    if (permissionsMap.canAddChildGoal) {
      const addChildGoal = this.buildAddChildGoalItem(menuItemBuilder);
      dropdownOptions.push(addChildGoal);
    }

    permissionsMap.areSuggestionsEnabled().then((isEnabled) => {
      if (isEnabled) {
        const suggestionsItem = this.buildSuggestionsItem(menuItemBuilder);
        dropdownOptions.splice(4, 0, suggestionsItem);
      }
    });

    const addFavorite = this.buildFavoriteItems(menuItemBuilder);
    dropdownOptions.push(...addFavorite);

    if (permissionsMap.canSeeHistory) {
      const seeHistory = this.buildSeeHistoryItem(menuItemBuilder);
      dropdownOptions.push(seeHistory);
    }

    const copyUrl = this.buildCopyUrlItem(menuItemBuilder);
    dropdownOptions.push(copyUrl);

    if (permissionsMap.canDeleteGoal) {
      const deleteItem = this.buildDeleteItem(menuItemBuilder);
      dropdownOptions.push(deleteItem);
    }

    if (permissionsMap.canSubmitToMarketplace) {
      const submitToMarketplace = this.buildSubmitToMarketplaceItem(menuItemBuilder);
      dropdownOptions.push(submitToMarketplace);
    }
    return dropdownOptions;
  }

  private buildAddMetricItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("add_key_result")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Add", parent: this.$scope.parentState })
      .setAction({ state: "._createMetric.createMetric", params: { id: this.$scope.goal.id } })
      .setClasses(["action bordered-bottom"])
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .build();
  }

  private buildSuggestionsItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("suggest_child_objectives")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Suggest Child Objectives", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.openQuantivePlusWizard(this.$scope.goal) })
      .setClasses(["action bordered-bottom okr-suggestion-item"])
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setSkipFromTabOrder()
      .build();
  }

  private buildCloneGoalItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("clone")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Clone", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.openCloneModal() })
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setClasses(["action bordered-bottom"])
      .setSkipFromTabOrder()
      .build();
  }

  private buildMoveItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("move")
      .setAction({ handler: (): void => this.openMoveModal() })
      .setClasses(["action bordered-bottom"])
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setSkipFromTabOrder()
      .build();
  }

  private buildAddChildGoalItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("add_child_goal")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Add subobjective", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.addChildObjective(this.$scope.goal.id, "goal", this.$scope.goal.sessionId) })
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setClasses(["action bordered-bottom"])
      .build();
  }

  private buildFavoriteItems(menuItemBuilder: DropdownMenuItemBuilder) {
    const isAddFavoritesVisible = new BehaviorSubject(!this.goalIsFavorite());
    const isRemoveFavoritesVisible = new BehaviorSubject(this.goalIsFavorite());
    const addFavorite = menuItemBuilder
      .setKey("add_to_favorites")
      .setTrack("New Experience Add to Favorites")
      .setTrackMetaData({ type: "goal", id: this.$scope.goal.id, source: "details-view" })
      .setAction({
        handler: (): void => {
          this.favoritesManager.addToFavorites(this.$scope.goal as { id: string }, "goal", this.getNavItem());
          isAddFavoritesVisible.next(false);
          isRemoveFavoritesVisible.next(true);
        },
      })
      .setIsVisibleCondition(isAddFavoritesVisible)
      .setClasses(["action add-favorite goal-favorite bordered-bottom d-flex"])
      .setSkipFromTabOrder()
      .build();
    const removeFavorite = menuItemBuilder
      .setKey("remove_from_favorites")
      .setTrack("New Experience Remove from Favorites")
      .setTrackMetaData({ type: "goal", id: this.$scope.goal.id, source: "details-view" })
      .setAction({
        handler: (): void => {
          this.favoritesManager.removeFromFavorites(this.$scope.goal as { id: string }, this.getNavItem());
          isAddFavoritesVisible.next(true);
          isRemoveFavoritesVisible.next(false);
        },
      })
      .setIsVisibleCondition(isRemoveFavoritesVisible)
      .setClasses(["action remove-favorite goal-favorite bordered-bottom d-flex align-items-c"])
      .setSkipFromTabOrder()
      .build();
    return [addFavorite, removeFavorite];
  }

  private buildSeeHistoryItem(menuItemBuilder: DropdownMenuItemBuilder) {
    let historyNgClass = "";
    if (this.$scope.planningSession.status !== "open" || this.$scope.goal.locked) {
      historyNgClass = "last-of-kind";
    }
    return menuItemBuilder
      .setKey("history")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "History", parent: this.$scope.parentState })
      .setAction({ state: "._timeline.timeline" })
      .setClasses([historyNgClass, "action"])
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setSkipFromTabOrder()
      .build();
  }

  private buildCopyUrlItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("copy_url")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Copy URL", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.copyItemUrlToClipboard(this.$scope.goal.id, "goal") })
      .setClasses(["action bordered-bottom bordered-top"])
      .build();
  }

  private buildDeleteItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("delete")
      .setTrack("Goal Form Menu Action Clicked")
      .setTrackMetaData({ type: "Delete", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.confirmDeleteGoal() })
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setToBeDestructive()
      .setSkipFromTabOrder()
      .build();
  }

  private buildSubmitToMarketplaceItem(menuItemBuilder: DropdownMenuItemBuilder) {
    return menuItemBuilder
      .setKey("submit_to_the_marketplace")
      .setAction({ state: "._marketplace.submit", params: { goalToBeSubmittedId: this.$scope.goal.id } })
      .setClasses(["action bordered-top to-marketplace"])
      .setIsVisibleCondition(of(this.$rootScope.hasPermission("ManageGoals")))
      .setUiType({ uiType: "button" })
      .setSkipFromTabOrder()
      .build();
  }

  private subscribeToDescriptionSubject(): void {
    this.$scope.descriptionInstance.subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.suggestAnother) {
        this.getQuantivePlusDescriptionSuggestion({ isRefresh: true });
      }

      if (data.refreshError) {
        this.getQuantivePlusDescriptionSuggestion();
      }

      if (data.addSuggestion) {
        this.onDescriptionSuggestionAdded();
      }

      if (data.removeSuggestion) {
        this.onDescriptionSuggestionRemoved();
      }
    });
  }
  private subscribeToTagsSubject(): void {
    this.$scope.tagsInstance.subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.suggestAnother) {
        this.getQuantivePlusTagsSuggestion({ isRefresh: true });
      }

      if (data.refreshError) {
        this.getQuantivePlusTagsSuggestion();
      }

      if (data.addSuggestion) {
        if (this.$scope.piContextualSuggestionsEnabled) {
          this.$scope.isTagsSuggestionVisible = false;
          this.$scope.$evalAsync();
        }

        this.onTagsSuggestionAdded();
      }

      if (data.removeSuggestion) {
        this.onTagsSuggestionRemoved();
        this.$scope.$digest();
      }
    });
  }

  private postKeyResultImprovement(payload, suggestionId: string): void {
    const suggestionIndex = this.$scope.suggestions.keyresults.findIndex((kr) => kr.id === suggestionId);
    this.$scope.suggestions.keyresults[suggestionIndex] = {
      ...this.$scope.suggestions.keyresults[suggestionIndex],
      isLoading: true,
    };

    this.$scope.$evalAsync();

    this.piImprovementFacade
      .postImprovement<IQuantivePlusMetric>(payload)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.$scope.suggestions.keyresults[suggestionIndex] = data.suggestions.improvement;
          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.suggestions.keyresults[suggestionIndex] = {
            ...this.$scope.suggestions.keyresults[suggestionIndex],
            isLoading: false,
            isError: true,
            errorData: getPiErrorData("metric", err),
          };
          this.$scope.$evalAsync();
        },
      });
  }

  private openGoalInAlignmentView = ($event): void => {
    if (!$event || $event.keyCode === "13") {
      this.$state.go("gtmhub.goals.alignmentSidebar.tree", { planningSessionId: this.$scope.goal.sessionId, locateId: this.$scope.goal.id }, { location: "replace" });
    }
  };

  private subscribeToDescriptionImprovement(): void {
    this.$scope.descriptionInstance.replacementSuggestionSubject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.piSuggestionSharedGoalStateService.replaceDescriptionSuggestion({
        description: data.newSuggestion.text,
        id: data.newSuggestion.id,
        suggestion: this.descriptionSuggestion,
      });
    });

    this.$scope.descriptionInstance.improvementSubject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const suggestion = this.$scope.descriptionSuggestedV2;

      const payload = generateImprovementPayload({
        subEntityType: this.$scope.descriptionInstance.subType,
        instruction: data.improvement,
        suggestion: {
          description: suggestion.text,
        },
        isAskAI: true,
        improveSuggestionContext: this.$scope.descriptionInstance.improveSuggestionContext,
      });

      this.piSuggestionSharedGoalStateService.postDescriptionImprovement(payload, this.descriptionSuggestion);
    });
  }

  private subscribeToTagsImprovement(): void {
    this.$scope.tagsInstance.replacementSuggestionSubject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const tagsClone = angular.copy(this.$scope.tagsSuggested);
      const tags = data.newSuggestion.text.split(tagsImprovementSplitOrJoinSuffix);
      tagsClone.forEach((tag, index) => (tag.title = tags[index]));

      this.piSuggestionSharedGoalStateService.replaceTagSuggestion(tagsClone, this.tagsSuggestion);
    });

    this.$scope.tagsInstance.improvementSubject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const payload = generateTagImprovementPayload({
        subEntityType: this.$scope.tagsInstance.subType,
        instruction: data.improvement,
        tags: this.$scope.tagsSuggested,
        isAskAI: true,
        improveSuggestionContext: this.$scope.tagsInstance.improveSuggestionContext,
      });

      this.piSuggestionSharedGoalStateService.postTagsImprovement(payload, tagsImprovementSplitOrJoinSuffix, this.tagsSuggestion);
    });
  }

  private setPIInstances(): void {
    const improveSuggestionContext: PIImproveSuggestionContext = {
      entityId: this.$scope.goal.id,
      parentId: this.$scope.goal.parentId,
      parentType: "objective",
    };
    this.$scope.descriptionInstance = this.piStateProcessorService.createNewInstance({
      type: "goal",
      subType: "description",
      isInline: true,
      improveSuggestionContext,
    });
    this.$scope.metricsInstance = this.piStateProcessorService.createNewInstance({
      type: "goal",
      subType: "metric",
      isInline: true,
      improveSuggestionContext,
    });
    this.$scope.tagsInstance = this.piStateProcessorService.createNewInstance({
      type: "goal",
      subType: "tags",
      isInline: true,
      improveSuggestionContext,
    });
    this.$scope.tasksInstance = this.piStateProcessorService.createNewInstance({
      type: "goal",
      subType: "task",
      isInline: true,
      improveSuggestionContext,
    });

    this.$scope.tasksInstance.subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.removeSuggestion) {
        this.onQuantivePlusTaskRejected(data.suggestion.index);
      }
    });
  }

  private setSuggestionDrawerInitialValues(): void {
    this.$scope.drawerExplanationSection = {
      isLoading: false,
      hasError: false,
      errorData: getPiErrorData("explanation", null),
      explanation: undefined,
      flowId: undefined,
      onRetry: () => this.loadSuggestionDrawerExplanation({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren }),
      refreshExplanation: () => this.refreshSuggestionDrawerExplanation({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren }),
    };
    this.$scope.drawerEffectivenessSection = {
      isLoading: false,
      hasError: false,
      errorData: getPiErrorData("effectiveness", null),
      effectiveness: undefined,
      onRetry: () => this.loadSuggestionDrawerEffectiveness({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren }),
      refreshEffectiveness: () => this.refreshSuggestionDrawerEffectiveness({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren }),
    };
    this.$scope.drawerTitleSection = {
      isLoading: false,
      isHidden: false,
      hasError: false,
      errorData: getPiErrorData("title", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [
        {
          id: "",
          suggestionText: "",
        },
      ],
      title: {
        isTitleAvailable: false,
        isSessionAvailable: false,
        isOwnerAvailable: false,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: () => this.setAcceptedTitleSuggestion(),
      onSuggestAnother: () =>
        this.loadTitleSuggestionPartOfEffectiveness({
          ...this.$scope.goal,
          goalChildren: this.$scope.goalChildren,
          isRefresh: true,
          rejected: { effectiveness: [{ title: this.$scope.drawerTitleSection.suggestions[0].suggestionText }] },
        }),
      onRetry: () => this.loadTitleSuggestionPartOfEffectiveness({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren, isRefresh: false, rejected: {} }),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableTitleSuggestion(),
    };
    this.$scope.drawerStatePropagator = new SuggestionDrawerIndicatorStatePropagator();
  }

  private setSuggestionDrawerSectionsImproveSuggestionContext = (): void => {
    let parentType: PIImprovementSuggestionType = "";

    if (this.$scope.goal.parentId) {
      parentType = this.$scope.goal.parentType === "goal" ? "objective" : "KR";
    }

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      improveSuggestionContext: {
        entityId: this.$scope.goal.id,
        parentId: this.$scope.goal.parentId,
        parentType,
      },
    };
  };

  private onQuantivePlusMetricRejected = (index: number): void => {
    const metric = this.$scope.suggestions.keyresults.at(index);
    if (!metric) {
      return;
    }

    metric.isHidden = true;
    this.$scope.$evalAsync();
  };

  private onQuantivePlusTaskRejected = (index: number): void => {
    const task = this.$scope.suggestions.tasks.at(index);
    if (!task) {
      return;
    }

    task.isHidden = true;
    this.$scope.$evalAsync();
  };

  private onQuantivePlusMetricCreated = (args: IQuantivePlusAcceptedMetric, flowName: ObjectiveFlowName = PiTrackingFlowNameEnum.ObjectiveDetailsView): void => {
    const suggestion = this.$scope.suggestions.keyresults[args.quantivePlusSuggestionIndex];
    this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, suggestion.id, "key-results", this.quantiveOKRsFlowId, flowName);

    this.$scope.suggestions.keyresults.splice(args.quantivePlusSuggestionIndex, 1);
    this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: [...this.$scope.goalChildren, { ...args.metric, childType: "metric" }] });

    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$rootScope.$broadcast("goalsChanged");
  };

  private goBackToLastAvailableMetricSuggestion = (args: { suggestionIndex: number }): void => {
    const suggestion = this.$scope.suggestions.keyresults[args.suggestionIndex];
    const clonedSuggestion = (({ description, format, id, initialValue, name, target, targetOperator }: IQuantivePlusMetric): IQuantivePlusMetric => ({
      description,
      format,
      id,
      initialValue,
      name,
      target,
      targetOperator,
    }))(suggestion);
    this.$scope.suggestions.keyresults[args.suggestionIndex] = clonedSuggestion;
    this.$scope.$evalAsync();
  };

  private addReturnStateForSharedUrl = (): void => {
    if (this.$stateParams.sharedGoal) {
      // the returnState is passed to _singleGoal() abstract state which uses the modal util method in routing
      const params = { ...this.$stateParams, sharedGoal: null, returnState: "^.^" };
      this.$state.go(".", params, { notify: false });
    }
  };

  private openCloneModal = (): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openCloneOkrModal(this.$scope.goal);
    } else {
      this.cloneOkrsModal.openForGoalClone(this.$scope.goal);
    }
  };

  private openMoveModal = (): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openMoveOkrModal(ResourceType.Objective, this.$scope.goal);
    } else {
      this.goalModalsService.openModalForMove("goal", this.$scope.goal);
    }
  };

  private updateName = (value: string) => {
    this.$rootScope.traceAction("update_OKR_fields-name", () => {
      this.patchUpdatedGoal(this.$scope.goal.id, { name: value }).then(() => {
        this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren, name: value });
      });
    });
  };

  private updateCustomFields = (customFields: CfMap) => {
    if (areCustomFieldsChanged(customFields, this.persistedGoalFields.customFields)) {
      const partialGoal = { customFields: customFields };
      this.patchUpdatedGoal(this.$scope.goal.id, partialGoal);
    }
  };

  private onDescriptionFocus = (): void => {
    if (this.$scope.piContextualSuggestionsEnabled) {
      this.$scope.isDescriptionFocused = true;
    }
  };

  private updateDescription = () => {
    if (this.persistedGoalFields.description !== this.$scope.goal.description) {
      const partialGoal = { description: this.$scope.goal.description };
      this.patchUpdatedGoal(this.$scope.goal.id, partialGoal).then(() => {
        this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren, ...partialGoal });
        this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));
      });
    }

    if (this.shouldFetchQuantivePlusDescriptionSuggestion()) {
      this.getQuantivePlusDescriptionSuggestion();
    }

    if (this.$scope.shouldFocusDescriptionTextEditor) {
      this.$scope.shouldFocusDescriptionTextEditor = false;
    }

    if (this.$scope.piContextualSuggestionsEnabled) {
      this.$scope.isDescriptionFocused = false;
    }

    this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));
  };

  private syncPersistedGoalFields(): void {
    this.persistedGoalFields.description = this.$scope.goal.description;
    this.persistedGoalFields.customFields = { ...this.$scope.goal.customFields };
  }

  private updateField = (goal: IGoal, fields: string[], additionalPayloadData: UpdateGoalAdditionalPayloadData = {}): IPromise<void> => {
    return this.$rootScope.traceAction(`update_OKR_fields-${fields.join("_")}`, () => {
      const partialGoal = {};
      for (let i = 0; i < fields.length; i++) {
        if (typeof fields[i].split(".")[1] !== "undefined") {
          const top = fields[i].split(".")[0];

          partialGoal[top] = goal[top];
        } else {
          partialGoal[fields[i]] = goal[fields[i]];
        }
      }

      return this.patchUpdatedGoal(goal.id, { ...partialGoal, ...additionalPayloadData });
    });
  };

  private patchUpdatedGoal = (goalId: string, patchGoal: Partial<IGoal> & UpdateGoalAdditionalPayloadData): IPromise<void> => {
    const includeMetrics = this.$state.current.name.includes(".tree.");
    // https://gtmhub.atlassian.net/browse/GVS-6795 - patchGoal method also broadcasts and loadData method is called twice
    return this.goalService.patchGoal(goalId, patchGoal, { includeMetrics: includeMetrics }).then(
      () => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");
        removeAllPersistedSuggestionsFeedback("goal", this.$scope.goal.id);
      },
      (error) => {
        this.$scope.indicators.loadingGoalProgress = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private reflectChangeWithinSuggestionStateIndicatorOnly = () => {
    this.$scope.drawerExplanationSection = {
      ...this.$scope.drawerExplanationSection,
      isLoading: true,
    };
    this.$timeout(() => {
      this.$scope.drawerExplanationSection = {
        ...this.$scope.drawerExplanationSection,
        isLoading: false,
      };
    }, 2000);
  };

  private reflectFieldChangeWithinSuggestionDrawer = (goalData: { ownerIds?: string[]; name?: string; description?: string; goalChildren: ContributorType[] }): void => {
    if (!this.$scope.isSuggestionStateIndicatorEnabled) return;

    this.loadSuggestionDrawerEffectiveness(goalData);

    if (!this.$scope.shouldDisplaySuggestionDrawer && !this.$scope.drawerEffectivenessSection.isLoading) {
      this.$scope.drawerExplanationSection = {
        ...this.$scope.drawerExplanationSection,
        explanation: undefined,
      };
      this.reflectChangeWithinSuggestionStateIndicatorOnly();

      return;
    }

    this.loadSuggestionDrawerExplanation(goalData);
  };

  private confirmDeleteGoal = (): void => {
    this.confirmGoalDeleteService.confirmGoalDelete({
      action: () => this.deleteGoal(),
    });
  };

  private deleteGoal = (): void => {
    this.$rootScope.traceAction("delete_OKR", () => {
      this.$scope.indicators.delete = { progress: true };
      this.goalService.deleteGoal(this.$scope.goal.id).then(
        () => {
          this.$rootScope.$broadcast("goalsChanged");
          this.$rootScope.$broadcast("goalDeleted", this.$scope.goal.id, this.$scope.goal);

          delete this.$scope.indicators.delete;
          this.$scope.$close();
        },
        () => {
          delete this.$scope.indicators.delete;
          this.$scope.error = "deleting";
        }
      );
    });
  };

  /**
   * If the plan is downgraded and the progress calculation criteria is NOT based on metrics - switch to metrics
   */
  private switchProgressCriteriaIfDowngradedPlan = (): void => {
    if (!this.$rootScope.featureAvailable("okrs.alignment.custom") && this.$scope.goal.attainmentTypeString !== "average_kr") {
      this.$scope.switchProgressCalculationCriteria("average_kr");
    }
  };

  private getGoal = (): IPromise<void> => {
    return this.goalService.getGoalWithFields(this.$stateParams.id).then((goal) => {
      return this.getParentItem(goal).then((goal) => {
        this.$scope.goal = { ...goal, ...(goal.metrics && { metrics: this.enrichChildMetrics(goal.metrics) }) };
        this.syncPersistedGoalFields();
        this.setInitialCustomFields(goal.customFields);
        this.switchProgressCriteriaIfDowngradedPlan();
        this.setProgressDescription();
        this.$scope.hasTeamInOwners = this.$scope.goal.assignees?.some((assignee) => assignee.type === "team") || false;

        this.$scope.goalPoliciesMap = toKeyMap("policy", this.$scope.goal.designScore.policies);
        this.drawCircularProgressBar(this.$scope.goal.designScore.totalPoints);

        for (const metric of this.$scope.goal.metrics || []) {
          if (!metric.ownerIds || !metric.ownerIds.length) {
            metric.ownerIds = this.$scope.goal.ownerIds;
          }
        }
        this.$scope.allowedActions = goal.currentUserAllowedActions;
        this.setGoalSupport();
      });
    });
  };

  private setGoalSupport() {
    // Sometimes after a goal is linked, there is no data for it in the get goal response
    // so we need to fetch it separately until the backend worker for linking completes and fills the data in 'links.expanded' field
    const hasSupportingLinks = !!this.$scope.goal.links;
    const hasDataForSupportingLinks = !!this.$scope.goal.links?.expanded;
    if (hasSupportingLinks && !hasDataForSupportingLinks) {
      this.fetchMissingLinksData();
    } else {
      this.$scope.goal.supporting = groupGoalsBySession(this.$scope.goal, "supporting");
    }

    // prettifying 'supported by' goals
    this.$scope.goal.supported = groupGoalsBySession(this.$scope.goal, "supported");
  }

  private setInitialCustomFields(customFields: CfMap) {
    if (!this.$scope.initCustomFields) {
      this.$scope.initCustomFields = { ...(customFields || {}) };
    }
  }

  private fetchMissingLinksData() {
    // There is only one supporting goal allowed, so we fetch it
    if (this.$scope.relatedItemsFeatureEnabled) {
      const itemInfo = this.$scope.goal.links.spec.relating[0];

      const itemPromise: IPromise<IGoal | Metric> =
        itemInfo.type === "goal"
          ? this.goalService.getGoalWithFields(itemInfo.id, "id,sessionId,name,assignee,attainment")
          : this.metricService.getMetricsV2({ fields: "id,sessionId,name,assignee,attainment,goalId", filter: { _id: itemInfo.id } }).then(({ items }) => items[0]);

      itemPromise.then((item) => {
        const session = this.$scope.allSessions.find((s) => s.id === item.sessionId);
        const isMetric = "goalId" in item;

        this.$scope.goal.links.expanded = {
          goals: !isMetric ? [item] : [],
          metrics: isMetric ? [item] : [],
          sessions: [session],
        };
        this.$scope.goal.supporting = groupGoalsBySession(this.$scope.goal, "supporting");
      });
    } else {
      this.goalService.getGoalWithFields(this.$scope.goal.links.spec.supporting[0].id, "id,sessionId,name,assignee,attainment").then((linkedGoal) => {
        const goalSession = this.$scope.allSessions.find((it) => it.id === linkedGoal.sessionId);

        this.$scope.goal.links.expanded = {
          goals: [linkedGoal],
          metrics: [],
          sessions: [goalSession],
        };

        this.$scope.goal.supporting = groupGoalsBySession(this.$scope.goal, "supporting");
      });
    }
  }

  private setProgressDescription = (): void => {
    this.$scope.progressDescription = getProgressDescription(this.progressColoring.getColoring(), this.$scope.goal.attainment, this.$scope.goal.color);
  };

  private setCanChangeClosedStatus = (): void => {
    this.$scope.canChangeClosedStatus =
      this.$scope.goal.sessionId &&
      this.$scope.planningSession.status !== "closed" &&
      (this.currentUserService.userHasRole("admin") ||
        this.$scope.goal.ownerIds?.includes(getCurrentUserId()) ||
        isGoalOwnedByTeamOfEmployee(this.$scope.employee).some((teamId) => this.$scope.goal.ownerIds.includes(teamId)));
  };

  private getAllPolicies = (): IPromise<void> => {
    return this.policyService.getPolicies().then((allPolicies) => {
      this.$scope.allPoliciesMap = toKeyMap("fieldName", allPolicies);
    });
  };

  private getReduxItems = (): IPromise<void> => {
    return this.$scope.watchUntilAndRejectOnError(
      () => this.$scope.reduxItemsFetched,
      () => this.$scope.reduxItemsError
    );
  };

  private getParentOkr = () => {
    const goalSession = this.$scope.allSessions.find((it) => it.id === this.$scope.goal.sessionId);
    this.$scope.sessionsForParentSelector = [goalSession?.id, goalSession?.parentId];
    this.$scope.okrForParentSelector = {
      id: this.$scope.goal.parentId,
      type: this.$scope.goal.parentType,
      isRestrictedParentSession: this.isRestrictedParentSession,
    };
  };

  private getParentItem(goal: IGoal): IPromise<IGoal> {
    const defer = this.$q.defer();

    if (!goal.parentId) {
      this.$scope.parentItem = null;
      return this.$q.resolve(goal);
    }

    if (goal.parentType === "goal") {
      /**
       * if parent objective has been rejected with 404 - it's most likely private and current user has no access to it
       * in that case we set a 'fake' parent objective so that we are able to show the form as usual and 'parent okr is not available'
       * message in parent selector
       */
      this.goalService
        .getGoal(goal.parentId)
        .then((parentGoal) => {
          this.$scope.parentItem = parentGoal;

          return defer.resolve(goal);
        })
        .catch((error) => {
          const parentGoalSession = this.$scope.allSessions.find((session) => session.id === goal.parentGoalSessionId);
          if (!parentGoalSession && error.status === 404) {
            const parentGoal = angular.copy(goal);
            this.$scope.parentItem = parentGoal;
            this.isRestrictedParentSession = true;
            return defer.resolve(goal);
          }

          return defer.reject(error);
        });

      return defer.promise;
    }

    return this.metricService.getMetric(goal.parentId).then((parentMetric) => {
      this.$scope.parentItem = parentMetric;
      return goal;
    });
  }

  private getSubGoalsIfNeeded = (): IPromise<void> => {
    if (this.$scope.goal.attainmentTypeString === "average_children" || this.$scope.goal.attainmentTypeString === "average_children_kr") {
      return this.goalService.getGoalsV2({ filter: { parentId: this.$scope.goal.id } }).then((subGoalsCollection) => {
        this.$scope.subGoals = this.enrichSubgoals(subGoalsCollection.items);
      });
    } else {
      return this.$q.resolve().then(() => {
        this.$scope.subGoals = [];
      });
    }
  };

  private getCurrentUserAllowedSessionActions = (): IPromise<void> => {
    if (!this.$scope.goal.sessionId) {
      this.$scope.canAccessSession = false;
      return this.$q.resolve();
    }

    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.goal.sessionId, "session").then((currentUserAllowedSessionActions) => {
      this.$scope.sessionAllowedActions = currentUserAllowedSessionActions.currentUserActions;
      this.$scope.canAccessSession = this.$scope.sessionAllowedActions && this.$scope.sessionAllowedActions.includes("read");
    });
  };

  private getWorkflowActionsIfNeeded = (): IPromise<void> => {
    if (this.$scope.goal.workflow) {
      return this.goalService.getGoalWorkflowActions(this.$scope.goal.id).then((workflowActions) => {
        this.$scope.workflowActions = workflowActions;
        this.$scope.hasWorkflow =
          workflowActions && !workflowActions.none && (workflowActions.approved || workflowActions.draft || workflowActions.needsReviewers || workflowActions.review);
      });
    }
  };

  private checkMarketplaceSubmissionFeatureToggle = (): void => {
    const { type } = this.accountResolverService.getAccountData();
    this.$scope.isAllowedToSubmitToMarketplace =
      this.accountService.featureAvailable("marketplace") &&
      (isGtmhubAccountId(this.accountResolverService.getAccountId()) || type === AccountType.PartnerAccount || type === AccountType.InternalAccount);
  };

  private checkRelatedItemsFeatureToggle = (): IPromise<void> => {
    const relatedItemsDefer = this.$q.defer<void>();

    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.RelatedItems)
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.relatedItemsFeatureEnabled = enabled;
          relatedItemsDefer.resolve();
        },
        error: () => {
          relatedItemsDefer.resolve();
        },
      });

    return relatedItemsDefer.promise;
  };

  private checkMandatoryOKRsAlignmentFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature).then((isMandatoryOkrAlignmentEnabled) => {
      const mandatoryOkrAlignmentSettings =
        this.accountService.getAccountSetting<IMandatoryOkrAlignmentSettings>("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();

      this.$scope.isOkrAlignmentMandatory =
        isMandatoryOkrAlignmentEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());
    });
  };

  private checkA14CloneAndMoveModalsFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.CloneAndMoveOkrModal).then((isNewCloneAndMoveModalEnabled) => {
      this.$scope.isNewCloneAndMoveModalEnabled = isNewCloneAndMoveModalEnabled;
    });
  };

  private checkSuggestionStateIndicatorFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isAtLeastOneTabVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionStateIndicatorEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private checkSuggestionDrawerOverviewTabFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isOverviewVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionDrawerOverviewTabEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private checkSuggestionDrawerEffectivenessTabFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isEffectivenessVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionDrawerEffectivenessTabEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private checkTagsFeatureToggle = (): IPromise<void> => {
    const tagsDefer = this.$q.defer<void>();

    this.featureToggleService
      .isFeatureAvailable$(ModuleFlag.Tags)
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isTagsModuleEnabled = enabled;
          tagsDefer.resolve();
        },
        error: () => {
          tagsDefer.resolve();
        },
      });

    return tagsDefer.promise;
  };

  private setSessionToScope = (): void => {
    if (this.$scope.goal.sessionId) {
      const foundPlanningSession = this.$scope.allSessions.find((s) => s.id === this.$scope.goal.sessionId);
      if (foundPlanningSession) {
        this.$rootScope.planningSession = foundPlanningSession;
        this.$scope.planningSession = foundPlanningSession;
      }
    }
  };

  private shouldFetchQuantivePlusDescriptionSuggestion = (): boolean => {
    return this.$scope.isSuggestingDescriptionToOKRsEnabled && this.$scope.allowEditGoalFields && !this.$scope.goal.description && !this.$scope.descriptionSuggested;
  };

  private shouldFetchQuantivePlusTagsSuggestion = (): boolean => {
    return (
      this.$scope.isSuggestingTagsToOKRsEnabled &&
      this.$scope.allowEditGoalFields &&
      this.$scope.isTagsModuleEnabled &&
      !this.$scope.goal.tags.length &&
      !this.$scope.tagsSuggested.length
    );
  };

  private shouldSuggestQuantivePlusMetrics = (): boolean => {
    const minKrsCountNotToShowKrsSuggestions = Math.min(this.$scope.methodologySettings.metricsPerGoalLimit, maxKrsSuggestions);

    return (
      this.$rootScope.hasPermission("ManageGoals") &&
      this.$scope.isSuggestingKRsToOKRsEnabled &&
      this.metricsContributeToGoalProgress() &&
      this.goalUnlockedUnabandonedSessionOpen() &&
      this.$scope.goalChildren &&
      this.$scope.goalChildren.length < minKrsCountNotToShowKrsSuggestions &&
      this.$scope.allowedActions.includes("create")
    );
  };

  private shouldSuggestQuantivePlusTasks = (): boolean => {
    return hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageTasks") && this.$scope.isSuggestingTasksToOKRsEnabled;
  };

  private onMetricCreated = (metric: Metric): void => {
    if (this.$scope.isSuggestingKRsToOKRsEnabled && this.$scope.suggestions) {
      this.$scope.suggestions.keyresults.shift();
    }

    this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: [...this.$scope.goalChildren, { ...metric, childType: "metric" }] });
  };

  private onGoalTaskAdded = (suggestionIndex?: number, currentGoalTasksCount?: number, flowName?: ObjectiveFlowName): void => {
    if (this.$scope.suggestions) {
      if (suggestionIndex || suggestionIndex === 0) {
        const suggestion = this.$scope.suggestions.tasks[suggestionIndex];
        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, suggestion.id, "tasks", this.quantiveOKRsFlowId, flowName);

        this.$scope.suggestions.tasks.splice(suggestionIndex, 1);
      } else {
        this.$scope.suggestions.tasks.shift();
      }

      if (currentGoalTasksCount >= minTasksCountNotToFetchSuggestions) {
        this.addQuantivePlusTaskSuggestion();
      }
    }
  };

  private onGoalTaskAddedMigrated = (event: { suggestionIndex?: number; currentGoalTasksCount?: number; flowName?: ObjectiveFlowName }): void => {
    if (this.$scope.suggestions) {
      if (event.suggestionIndex || event.suggestionIndex === 0) {
        const suggestion = this.$scope.suggestions.tasks[event.suggestionIndex];
        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, suggestion.id, "tasks", this.quantiveOKRsFlowId, event.flowName);

        this.$scope.suggestions.tasks.splice(event.suggestionIndex, 1);
      } else {
        this.$scope.suggestions.tasks.shift();
      }

      if (event.currentGoalTasksCount >= minTasksCountNotToFetchSuggestions) {
        this.addQuantivePlusTaskSuggestion();
      }
    }
  };

  private onGoalTaskRemoved = (currentGoalTasksCount: number): void => {
    if (this.$scope.isSuggestingTasksToOKRsEnabled && currentGoalTasksCount + this.$scope.suggestions?.tasks?.length < minTasksCountNotToFetchSuggestions) {
      this.addQuantivePlusTaskSuggestion();
    }
  };

  private onGoalTaskRemovedMigrated = (event: { currentGoalTasksCount: number }): void => {
    if (this.$scope.isSuggestingTasksToOKRsEnabled && event.currentGoalTasksCount + this.$scope.suggestions?.tasks?.length < minTasksCountNotToFetchSuggestions) {
      this.addQuantivePlusTaskSuggestion();
    }
  };

  private addQuantivePlusMetricSuggestion = (): void => {
    if (!this.$scope.suggestions) {
      this.$scope.suggestions = { sessionId: this.$scope.goal.sessionId, tasks: [], keyresults: [] };
    }

    const dummyMetricSuggestionToShowAsLoading: IQuantivePlusMetric = generateDummyMetricSuggestion({ isLoading: true });
    this.$scope.suggestions.keyresults.push(dummyMetricSuggestionToShowAsLoading);

    const existingKrs = getExistingKrsFromGoal(this.$scope.goalChildren);
    const payload = generateQuantivePlusSuggestionsPayload({
      subEntityType: "metricsAndTasks",
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      existingKrs: existingKrs,
      teams: this.$scope.teams,
      nSuggestions: 1,
    });

    this.quantivePlusSuggestionsFacade
      .getQuantivePlusKrSuggestion(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (suggestions) => {
          const metricSuggestion = suggestions.suggestions.keyresults[0];
          const firstDummyLoadingMetricIndex = this.$scope.suggestions.keyresults.findIndex((metric) => metric.isLoading);
          this.$scope.suggestions.keyresults[firstDummyLoadingMetricIndex] = metricSuggestion;

          this.$scope.$digest();
        },
        error: (err: HttpErrorResponse) => {
          const dummyMetricSuggestionToShowAsError: IQuantivePlusMetric = generateDummyMetricSuggestion({
            isError: true,
            errorData: getPiErrorData("metric", err),
          });

          const firstDummyLoadingMetricIndex = this.$scope.suggestions.keyresults.findIndex((metric) => metric.isLoading);
          this.$scope.suggestions.keyresults[firstDummyLoadingMetricIndex] = dummyMetricSuggestionToShowAsError;
        },
      });
  };

  private addQuantivePlusTaskSuggestion = (): void => {
    if (!this.$scope.suggestions) {
      this.$scope.suggestions = { sessionId: this.$scope.goal.sessionId, tasks: [], keyresults: [] };
    }

    const dummyTaskSuggestionToShowAsLoading: IQuantivePlusTask = generateDummyTaskSuggestion({ isLoading: true });
    this.$scope.suggestions.tasks.push(dummyTaskSuggestionToShowAsLoading);

    const onError = (err: HttpErrorResponse): void => {
      const dummyTaskSuggestionToShowAsError: IQuantivePlusTask = generateDummyTaskSuggestion({
        isError: true,
        errorData: getPiErrorData("task", err),
      });

      const firstDummyLoadingTaskIndex = this.$scope.suggestions.tasks.findIndex((task) => task.isLoading);
      this.$scope.suggestions.tasks[firstDummyLoadingTaskIndex] = dummyTaskSuggestionToShowAsError;
      this.$scope.$digest();
    };

    this.getGoalTasks().then(
      (result) => {
        const existingKrs = getExistingKrsFromGoal(this.$scope.goalChildren);
        const payload = generateQuantivePlusSuggestionsPayload({
          subEntityType: "metricsAndTasks",
          goal: this.$scope.goal,
          assigneesMap: this.$scope.assigneesIdMap,
          existingKrs: existingKrs,
          tasks: result.items,
          teams: this.$scope.teams,
          nSuggestions: 1,
        });

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrSuggestion(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (suggestions) => {
              const taskSuggestion = suggestions.suggestions.tasks[0];
              const firstDummyLoadingTaskIndex = this.$scope.suggestions.tasks.findIndex((task) => task.isLoading);
              this.$scope.suggestions.tasks[firstDummyLoadingTaskIndex] = taskSuggestion;

              this.$scope.$digest();
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private getGoalTasks = (): IPromise<ICollection<Task>> => {
    return this.taskService.getTasksV2({
      filter: { $and: [{ parentId: this.$scope.goal.id }, { $or: [{ archived: { $exists: false } }, { archived: false }] }] },
      sort: "-dueDate,dateCreated",
    });
  };

  private setKrsSuggestionsExpected = (): void => {
    const minKrsCountNotToShowKrsSuggestions = Math.min(this.$scope.methodologySettings.metricsPerGoalLimit, maxKrsSuggestions);

    if (this.$scope.goalChildren.length < minKrsCountNotToShowKrsSuggestions) {
      this.$scope.krsSuggestionsExpected = minKrsCountNotToShowKrsSuggestions - this.$scope.goalChildren.length;
    } else {
      this.$scope.krsSuggestionsExpected = 0;
    }
  };

  private getQuantivePlusSuggestionsPayload = (goalTasks: Task[]): PIKrSuggestionPayload => {
    const existingKrs = getExistingKrsFromGoal(this.$scope.goalChildren);
    return generateQuantivePlusSuggestionsPayload({
      subEntityType: "metricsAndTasks",
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      existingKrs: existingKrs,
      tasks: goalTasks,
      teams: this.$scope.teams,
    });
  };

  private getQuantivePlusSuggestions = (): void => {
    this.$scope.suggestionsStatus.metricsAndTasks = { isLoading: true };
    this.setKrsSuggestionsExpected();

    const onError = (err: HttpErrorResponse) => {
      this.$scope.suggestionsStatus.metricsAndTasks = {
        isError: true,
        errorDataMetrics: getPiErrorData("metrics", err),
        errorDataTasks: getPiErrorData("tasks", err),
      };

      this.$scope.$digest();
    };

    this.getGoalTasks().then(
      (result) => {
        const goalTasks = result.items;
        const payload = this.getQuantivePlusSuggestionsPayload(goalTasks);

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrSuggestion(payload)
          .pipe(take(1))
          .subscribe({
            next: (suggestions) => {
              this.$scope.suggestionsStatus.metricsAndTasks = {};

              if (this.$scope.krsSuggestionsExpected > 0) {
                suggestions.suggestions.keyresults = suggestions.suggestions.keyresults.slice(0, this.$scope.krsSuggestionsExpected);
              } else {
                suggestions.suggestions.keyresults = [];
              }

              if (goalTasks.length < minTasksCountNotToFetchSuggestions) {
                suggestions.suggestions.tasks = suggestions.suggestions.tasks.slice(0, minTasksCountNotToFetchSuggestions - goalTasks.length);
              } else {
                const tasksToSuggest = minTasksCountNotToFetchSuggestions - goalTasks.length;
                suggestions.suggestions.tasks = suggestions.suggestions.tasks.slice(0, goalTasks.length >= minTasksCountNotToFetchSuggestions ? 1 : tasksToSuggest);
              }

              this.$scope.suggestions = {
                sessionId: this.$scope.goal.sessionId,
                tasks: suggestions.suggestions.tasks,
                keyresults: suggestions.suggestions.keyresults,
              };

              this.piSuggestionSharedGoalStateService.setTasks(this.$scope.suggestions);
              this.piSuggestionSharedGoalStateService.setKeyResults(this.$scope.suggestions.keyresults);

              this.quantiveOKRsFlowId = suggestions.flowId;
              this.piSuggestionSharedGoalStateService.setQuantiveSuggestionsFlowId(suggestions.flowId);

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.metricsInstance.id, {
                flowId: this.quantiveOKRsFlowId,
                flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
              });

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.tasksInstance.id, {
                flowId: this.quantiveOKRsFlowId,
                flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
              });

              this.$scope.$digest();
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private setQuantivePlusSuggestionLoadingState = (args: IQuantivePlusRefreshedArgs, flowName: ObjectiveFlowName): void => {
    if (args.suggestionType === "metric") {
      const suggestion = this.$scope.suggestions.keyresults[args.suggestionToReplaceIndex];
      if (!args.suggestionToReplace.isError) {
        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionRefreshed, suggestion.id, "key-results", this.quantiveOKRsFlowId, flowName);
      }

      const dummyMetricSuggestionToShowAsLoading: IQuantivePlusMetric = generateDummyMetricSuggestion({ isLoading: true });
      this.$scope.suggestions.keyresults[args.suggestionToReplaceIndex] = { ...dummyMetricSuggestionToShowAsLoading, isHidden: suggestion.isHidden };
    }

    if (args.suggestionType === "task") {
      const suggestion = this.$scope.suggestions.tasks[args.suggestionToReplaceIndex];
      if (!args.suggestionToReplace.isError) {
        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionRefreshed, suggestion.id, "tasks", this.quantiveOKRsFlowId, flowName);
      }

      const dummyTaskSuggestionToShowAsLoading: IQuantivePlusTask = generateDummyTaskSuggestion({ isLoading: true });
      this.$scope.suggestions.tasks[args.suggestionToReplaceIndex] = { ...dummyTaskSuggestionToShowAsLoading, isHidden: suggestion.isHidden };
    }

    this.$scope.$digest();
  };

  private changeQuantivePlusSuggestion = (args: IQuantivePlusRefreshedArgs, flowName: ObjectiveFlowName = PiTrackingFlowNameEnum.ObjectiveDetailsView): void => {
    this.setQuantivePlusSuggestionLoadingState(args, flowName);

    const onError = (err: HttpErrorResponse) => {
      if (args.suggestionType === "metric") {
        const suggestion = this.$scope.suggestions.keyresults[args.suggestionToReplaceIndex];
        const dummyMetricSuggestionToShowAsError: IQuantivePlusMetric = generateDummyMetricSuggestion({
          isError: true,
          errorData: getPiErrorData("metric", err),
        });

        this.$scope.suggestions.keyresults[args.suggestionToReplaceIndex] = { ...dummyMetricSuggestionToShowAsError, isHidden: suggestion.isHidden };
      }

      if (args.suggestionType === "task") {
        const suggestion = this.$scope.suggestions.tasks[args.suggestionToReplaceIndex];
        const dummyTaskSuggestionToShowAsError: IQuantivePlusTask = generateDummyTaskSuggestion({
          isError: true,
          errorData: getPiErrorData("task", err),
        });
        this.$scope.suggestions.tasks[args.suggestionToReplaceIndex] = { ...dummyTaskSuggestionToShowAsError, isHidden: suggestion.isHidden };
      }

      this.$scope.$digest();
    };

    this.getGoalTasks().then(
      (result) => {
        const existingKrs = getExistingKrsFromGoal(this.$scope.goalChildren);
        const payload = generateQuantivePlusSuggestionsPayload({
          subEntityType: "metricsAndTasks",
          goal: this.$scope.goal,
          assigneesMap: this.$scope.assigneesIdMap,
          existingKrs: existingKrs,
          tasks: result.items,
          teams: this.$scope.teams,
          nSuggestions: 1,
          ...(!args.suggestionToReplace.isError && {
            isRefresh: true,
          }),
          ...(!args.suggestionToReplace.isError && {
            ...(args.suggestionType === "metric" && {
              rejectedKrs: [<IQuantivePlusMetric>args.suggestionToReplace],
            }),
            ...(args.suggestionType === "task" && {
              rejectedTasks: [<IQuantivePlusTask>args.suggestionToReplace],
            }),
          }),
        });

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrSuggestion(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (suggestions) => {
              if (args.suggestionType === "metric") {
                this.$scope.suggestions.keyresults[args.suggestionToReplaceIndex] = suggestions.suggestions.keyresults[0];
              }

              if (args.suggestionType === "task") {
                this.$scope.suggestions.tasks[args.suggestionToReplaceIndex] = suggestions.suggestions.tasks[0];
              }

              this.quantiveOKRsFlowId = suggestions.flowId;
              this.piSuggestionSharedGoalStateService.setQuantiveSuggestionsFlowId(suggestions.flowId);

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.metricsInstance.id, {
                flowId: this.quantiveOKRsFlowId,
                flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
              });

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.tasksInstance.id, {
                flowId: this.quantiveOKRsFlowId,
                flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
              });

              this.$scope.$evalAsync();
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private setSuggestionListeners = () => {
    this.$scope.$watchCollection("suggestions.keyresults", (newValue: IQuantivePlusMetric[]) => {
      this.piSuggestionSharedGoalStateService.setKeyResults(newValue);
    });
    this.$scope.$watchCollection("suggestions.tasks", () => {
      this.piSuggestionSharedGoalStateService.setTasks(this.$scope.suggestions);
    });
    this.$scope.$watchCollection("suggestionsStatus.metricsAndTasks", (status: IQuantivePlusMetricsAndTasksSuggestionStatus) => {
      this.piSuggestionSharedGoalStateService.setMetricsAndTasksSuggestionStatus(status);
      this.$scope.suggestionsStatus.metrics = generateMetricsSuggestionsStatus(status);
      this.$scope.suggestionsStatus.tasks = generateTasksSuggestionsStatus(status);
    });

    this.piSuggestionSharedGoalStateService
      .getRefreshAllKeyResults$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.getQuantivePlusSuggestions();
        },
      });

    this.piSuggestionSharedGoalStateService
      .getSingleMetric$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (!data.args) {
            return;
          }

          if (data.type === "replacing") {
            const clonedSuggestions = angular.copy(this.$scope.suggestions.keyresults);
            const suggestion = clonedSuggestions[(data.args as IQuantivePlusRefreshedArgs).suggestionToReplaceIndex];
            suggestion.name = data.newSuggestionName;

            this.$scope.suggestions.keyresults = clonedSuggestions;
            this.$scope.$evalAsync();

            return;
          }

          if (data.type === "improving") {
            const suggestion = angular.copy(this.$scope.suggestions.keyresults[(data.args as IQuantivePlusRefreshedArgs).suggestionToReplaceIndex]);
            delete suggestion.isLoading;

            const payload = generateImprovementPayload({
              subEntityType: this.$scope.metricsInstance.subType,
              instruction: data.improvement,
              suggestion: suggestion,
              isAskAI: true,
              improveSuggestionContext: data.improveSuggestionContext,
            });

            this.postKeyResultImprovement(payload, suggestion.id);
          }

          if (data.type === "refreshed") {
            this.changeQuantivePlusSuggestion(data.args as IQuantivePlusRefreshedArgs, PiTrackingFlowNameEnum.ObjectiveDetailsViewAllSuggestionSidePanel);
          }
          if (data.type === "accepted") {
            this.onQuantivePlusMetricCreated(data.args as IQuantivePlusAcceptedMetric, PiTrackingFlowNameEnum.ObjectiveDetailsViewAllSuggestionSidePanel);
          }
        },
      });

    this.piSuggestionSharedGoalStateService
      .getGoBackToLastSuggestion$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (args) => {
          this.goBackToLastAvailableMetricSuggestion({ suggestionIndex: args.quantivePlusSuggestionIndex });
        },
      });

    const descriptionSubscription = this.piSuggestionSharedGoalStateService
      .getDescription$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data.hasAcceptedDescription) {
            this.$scope.suggestionsStatus.description.isLoading = true;

            this.onDescriptionSuggestionAdded(PiTrackingFlowNameEnum.ObjectiveDetailsViewAllSuggestionSidePanel, data.description);

            this.$timeout(() => {
              this.$scope.suggestionsStatus.description.isLoading = false;
            }, 200);

            this.$scope.$digest();
            this.$scope.$evalAsync();

            return;
          }

          if (!data.suggestion) {
            this.$scope.suggestionsStatus.description = {
              isLoading: data.isLoading,
              isError: data.isError,
              errorData: data.errorData,
            };
            this.$scope.$evalAsync();

            return;
          }

          this.$scope.suggestionsStatus.description = {};

          this.currentDescriptionSuggestions = data.suggestion.suggestions[0].descriptions;
          this.descriptionSuggestion = data.suggestion;

          const descriptionTextSuggested = data.suggestion.suggestions[0].descriptions[0];
          this.$scope.descriptionSuggested = descriptionTextSuggested.description;

          this.$scope.descriptionSuggestedV2 = {
            id: descriptionTextSuggested.id,
            text: descriptionTextSuggested.description,
          };

          this.quantiveDescriptionFlowId = data.suggestion.flowId;

          this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.descriptionInstance.id, {
            flowId: this.quantiveDescriptionFlowId,
            flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
          });

          this.$scope.$evalAsync();
        },
      });

    descriptionSubscription.add(() => this.piSuggestionSharedGoalStateService.resetDescription());

    const tagsSubscription = this.piSuggestionSharedGoalStateService
      .getTags$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data.hasAcceptedTags) {
            this.onTagsSuggestionAdded(PiTrackingFlowNameEnum.ObjectiveDetailsViewAllSuggestionSidePanel, data.tags);

            return;
          }

          if (!data.suggestion) {
            this.$scope.suggestionsStatus.tags = {
              isLoading: data.isLoading,
              isError: data.isError,
              errorData: data.errorData,
            };
            this.$scope.$evalAsync();

            return;
          }

          this.$scope.suggestionsStatus.tags = {};
          this.$scope.tagsSuggested = data.suggestion.suggestions.tags;
          this.$scope.tagsSuggestedV2 = data.suggestion.suggestions.tags.map((tag) => tag.title);

          this.$scope.tagsSuggestion = {
            id: data.suggestion.suggestions.tags[0].id,
            text: this.$scope.tagsSuggestedV2,
          };

          this.tagsSuggestion = data.suggestion;
          this.quantiveTagsId = data.suggestion.flowId;

          this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.tagsInstance.id, {
            flowId: this.quantiveTagsId,
            flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
          });
          this.$scope.$evalAsync();
        },
      });

    tagsSubscription.add(() => this.piSuggestionSharedGoalStateService.resetTags());
  };

  private getQuantivePlusDescriptionSuggestion = (args?: { isRefresh: boolean }): void => {
    const payload = generateQuantivePlusObjectiveDescriptionSuggestionPayload({
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      ...(args?.isRefresh && {
        isRefresh: true,
      }),
      ...(args?.isRefresh && {
        rejectedDescriptions: this.currentDescriptionSuggestions,
      }),
    });

    this.piSuggestionSharedGoalStateService.getDescription({
      suggestionId: this.$scope.descriptionSuggestedV2?.id,
      payload,
      flowId: this.quantiveDescriptionFlowId,
      flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
      isRefresh: args?.isRefresh,
    });
  };

  private onDescriptionSuggestionRemoved = (args?: { isError: boolean }): void => {
    this.$scope.suggestionsStatus.description = {};
    this.$scope.descriptionSuggested = "";
    this.currentDescriptionSuggestions = [];
    this.$scope.shouldFocusDescriptionTextEditor = !this.$scope.piContextualSuggestionsEnabled;

    this.$scope.$evalAsync();

    if (this.$scope.piContextualSuggestionsEnabled) {
      this.$scope.setDescriptionSuggestionVisibility();
    }

    if (args?.isError) {
      return;
    }

    this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionRejected, this.$scope.descriptionSuggestedV2.id, "description", this.quantiveDescriptionFlowId);
  };

  private goBackToLastAvailableDescriptionSuggestion = (): void => {
    this.piSuggestionSharedGoalStateService.goBackToLastAvailableDescriptionSuggestion(this.descriptionSuggestion);
  };

  private sendQuantivePlusTracking = (
    eventType: PiTrackingEventsEnum,
    suggestionId: string,
    suggestedEntity: "description" | "tags" | "key-results" | "tasks",
    flowId: string,
    flowName: ObjectiveFlowName = PiTrackingFlowNameEnum.ObjectiveDetailsView
  ): void => {
    this.analyticsService.track(eventType, {
      suggestedEntity: suggestedEntity,
      suggestionId,
      flowName: flowName,
      flowId: flowId,
      userId: getCurrentUserId(),
    });
  };

  private onDescriptionSuggestionAdded = (flowName: ObjectiveFlowName = PiTrackingFlowNameEnum.ObjectiveDetailsView, description?: string): void => {
    this.$scope.shouldFocusDescriptionTextEditor = true;
    this.$scope.goal.description = this.$scope.descriptionSuggested || description;
    this.$scope.descriptionSuggested = "";
    this.currentDescriptionSuggestions = [];
    this.updateField(this.$scope.goal, ["description"], { flowId: this.quantiveDescriptionFlowId, flowName }).then(() => {
      this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren });
    });

    this.sendQuantivePlusTracking(
      PiTrackingEventsEnum.PiSuggestionAccepted,
      this.$scope.descriptionSuggestedV2.id,
      "description",
      this.quantiveDescriptionFlowId,
      flowName
    );
  };

  private getQuantivePlusTagsSuggestion = (args?: { isRefresh: boolean }): void => {
    const payload = generateQuantivePlusObjectiveTagsSuggestionPayload({
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      ...(args?.isRefresh && {
        isRefresh: true,
      }),
      ...(args?.isRefresh && {
        rejectedTags: this.$scope.tagsSuggested,
      }),
    });

    this.piSuggestionSharedGoalStateService.getTags({
      suggestionId: this.$scope.tagsSuggestion?.id,
      payload,
      flowId: this.quantiveTagsId,
      flowName: PiTrackingFlowNameEnum.ObjectiveDetailsView,
      isRefresh: args?.isRefresh,
    });
  };

  private onTagsSuggestionRemoved = (args?: { isError: boolean }): void => {
    this.$scope.tagsSuggested = [];
    this.$scope.tagsSuggestedV2 = [];
    this.$scope.suggestionsStatus.tags = {};

    this.$scope.$evalAsync();

    if (!this.$scope.piContextualSuggestionsEnabled) {
      this.focusGoalTagsInput();
    }

    if (this.$scope.piContextualSuggestionsEnabled) {
      this.$scope.setTagsSuggestionVisibility();
    }

    if (args?.isError) {
      return;
    }

    this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionRejected, this.$scope.tagsSuggestion.id, "tags", this.quantiveTagsId);
  };

  /*
    Why we need a combination of both the "handleTagsInputBlur()" and the "onTagsInputOpenChange()" methods
    for handling the fetching a new group of tags suggestions on tags input blur:
      1) if you clear the suggestion, this would focus the tags input, but if you then click directly outside of the tags selector - this won't trigger
    the "onTagsInputOpenChange" method, thus a new group of suggestions won't be fetched - here we need the "handleTagsInputBlur" method;
      2) if we clear the suggestion and then try to click on the dropdown list of existing tags - this blurs the tags input and new suggestions
    are fetched immediately. To prevent that, we need the "onTagsInputOpenChange" method, which in this case knows that the tags input is still opened.
  */
  private handleTagsInputBlur = (): void => {
    // the setTimeout is needed as the value of "this.isTagsInputOpen" is still not updated at the moment of executing the "handleTagsInputBlur" logic
    this.$timeout(() => {
      if (!this.isTagsInputOpen && this.shouldFetchQuantivePlusTagsSuggestion()) {
        this.getQuantivePlusTagsSuggestion();
        this.$scope.shouldFocusTagsInput = false;
        this.$scope.shouldOpenTagsInput = false;
        this.isTagsInputOpen = false;
        this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));
        this.$scope.$digest();
      }
    }, 300);
  };

  private onTagsInputOpenChange = (isOpen: boolean): void => {
    if (!this.$scope.isSuggestingTagsToOKRsEnabled || !this.$scope.allowEditGoalFields) {
      return;
    }

    this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));

    this.isTagsInputOpen = isOpen;
    const goalTagsUiSelectElement = document.querySelector("#goal-main-info-tags-selector").querySelector("ui-select");

    if (!goalTagsUiSelectElement.classList.contains("cdk-focused") && this.shouldFetchQuantivePlusTagsSuggestion()) {
      this.getQuantivePlusTagsSuggestion();
      this.$scope.shouldFocusTagsInput = false;
      this.$scope.shouldOpenTagsInput = false;
      this.isTagsInputOpen = false;
    }

    if (this.$scope.piContextualSuggestionsEnabled) {
      this.$scope.isTagsFocused = isOpen;
      this.$scope.$digest();
    }
  };

  private focusGoalTagsInput = () => {
    this.$timeout(() => {
      this.$scope.shouldFocusTagsInput = true;
      this.$scope.shouldOpenTagsInput = true;
      this.isTagsInputOpen = true;

      this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));

      this.$scope.$digest();
    }, 550);
  };

  private onTagsSuggestionAdded = (flowName: ObjectiveFlowName = PiTrackingFlowNameEnum.ObjectiveDetailsView, tags?: ITag[]): void => {
    const tagsToAdd: ITag[] = this.$scope.tagsSuggested.length ? this.$scope.tagsSuggested : tags;
    const flowData: UpdateGoalAdditionalPayloadData = {
      flowId: this.quantiveTagsId,
      flowName,
    };

    this.$scope.tagsSuggested = [];
    this.$scope.tagsSuggestedV2 = [];

    this.$scope.$digest();
    this.$scope.$broadcast("tags-suggestion-added", tagsToAdd, flowData);
    this.piSuggestionSharedGoalStateService.setGoal(angular.copy(this.$scope.goal));
    this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, this.$scope.tagsSuggestion.id, "tags", this.quantiveTagsId, flowName);

    this.$scope.$evalAsync();
    this.focusGoalTagsInput();
  };

  private goBackToLastAvailableTagsSuggestion = (): void => {
    this.piSuggestionSharedGoalStateService.goBackToLastAvailableTagsSuggestion(this.tagsSuggestion);
  };

  public openQuantivePlusWizard = (goal: IGoal): void => {
    this.modalService.create<GuidedOKRsComponent, GuidedOKRsModalData>({
      uiContent: GuidedOKRsComponent,
      uiWrapClassName: "guided-okrs-modal-wrapper",
      uiFooter: null,
      uiClosable: false,
      uiKeyboard: false,
      uiData: {
        currentSession: this.$scope.allSessions.find((s) => s.id === goal.sessionId),
        readOnlySession: true,
        flowName: PiTrackingFlowNameEnum.GuidedOkrsCreationOkrViewDropdown,
        parent: {
          id: this.$scope.goal.id,
          title: this.$scope.goal.name,
          ownerIds: this.$scope.goal.ownerIds,
        },
      },
    });
  };

  private changeSuggestionDrawerVisibility = (isVisible: boolean): void => {
    this.$scope.shouldDisplaySuggestionDrawer = isVisible;
    this.$scope.$evalAsync();

    if (!this.$scope.drawerExplanationSection.explanation) {
      this.loadSuggestionDrawerExplanation({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren });
    }
  };

  private generateQuantivePlusObjectivesExplanationPayload = ({
    ownerIds,
    name,
    description,
    goalChildren,
  }: {
    ownerIds?: string[];
    name?: string;
    description?: string;
    goalChildren: ContributorType[];
  }): QuantivePlusObjectivesExplanationPayload => {
    const goalAssignees = generateAssignees(ownerIds, this.$scope.assigneesIdMap, this.$scope.teams);
    const existingKrs = getExistingKrsFromGoal(goalChildren);

    return {
      entityId: this.$scope.goal.id,
      subEntityType: "overview",
      refresh: false,
      objectives: [
        {
          title: name,
          description: description,
          assignees: goalAssignees,
          existingKrs,
          accepted: {},
          rejected: {},
        },
      ],
    };
  };

  private generateQuantivePlusObjectivesEffectivenessPayload = ({
    ownerIds,
    name,
    description,
    goalChildren,
    refresh,
    accepted,
    rejected,
    entityId,
    subEntityType,
  }: {
    ownerIds?: string[];
    name?: string;
    description?: string;
    goalChildren: ContributorType[];
    refresh: boolean;
    accepted: SuggestedEffectivenessTitle;
    rejected: SuggestedEffectivenessTitle;
    entityId: string;
    subEntityType: PIStateProcessorInstanceSubType;
  }): QuantivePlusObjectivesEffectivenessPayload => {
    return generateQuantivePlusObjectivesEffectivenessPayload({
      name,
      description,
      ownerIds,
      childrenOfKrType: goalChildren.filter((child) => child.childType === "metric") as Metric[],
      assigneesIdMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      refresh,
      accepted,
      rejected,
      entityId,
      subEntityType,
    });
  };

  private loadSuggestionDrawerExplanation(goalData: { ownerIds?: string[]; name?: string; description?: string; goalChildren: ContributorType[] }): void {
    if (!this.$scope.isSuggestionDrawerOverviewTabEnabled) return;

    this.$scope.drawerExplanationSection = {
      ...this.$scope.drawerExplanationSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    const payload = this.generateQuantivePlusObjectivesExplanationPayload(goalData);

    this.quantivePlusExplanationFacade
      .getQuantivePlusObjectivesExplanation(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (explanationRes) => {
          this.$scope.drawerExplanationSection = {
            ...this.$scope.drawerExplanationSection,
            explanation: explanationRes.suggestions[0].explanation[0],
            flowId: explanationRes.flowId,
            hasError: false,
            isLoading: false,
          };
          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerExplanationSection = {
            ...this.$scope.drawerExplanationSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("explanation", err),
          };
          this.$scope.$evalAsync();
        },
      });
  }

  private refreshSuggestionDrawerExplanation(goalData: { ownerIds?: string[]; name?: string; description?: string; goalChildren: ContributorType[] }): void {
    if (!this.$scope.isSuggestionDrawerOverviewTabEnabled) return;

    const payload = this.generateQuantivePlusObjectivesExplanationPayload(goalData);

    this.quantivePlusExplanationFacade
      .getQuantivePlusObjectivesExplanation({ ...payload, refresh: true })
      .pipe(take(1))
      .subscribe();
  }

  private refreshSuggestionDrawerEffectiveness(goalData: Pick<IGoal, "ownerIds" | "name" | "description"> & { goalChildren: ContributorType[] }): void {
    if (!this.$scope.isSuggestionDrawerEffectivenessTabEnabled) return;

    const payload = this.generateQuantivePlusObjectivesEffectivenessPayload({
      ...goalData,
      refresh: true,
      accepted: {},
      rejected: {},
      entityId: this.$scope.goal.id,
      subEntityType: "effectiveness",
    });

    // just a polling request to refresh the cache on backend, we don't want to update UI in this specific case
    this.quantivePlusEffectivenessFacade.getQuantivePlusObjectivesEffectiveness(payload).pipe(take(1)).subscribe();
  }

  private loadSuggestionDrawerEffectiveness(goalData: Pick<IGoal, "ownerIds" | "name" | "description"> & { goalChildren: ContributorType[] }): void {
    if (!this.$scope.isSuggestionDrawerEffectivenessTabEnabled) return;

    this.$scope.drawerEffectivenessSection = {
      ...this.$scope.drawerEffectivenessSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    const payload = this.generateQuantivePlusObjectivesEffectivenessPayload({
      ...goalData,
      refresh: false,
      entityId: this.$scope.goal.id,
      subEntityType: "effectiveness",
      accepted: {},
      rejected: {},
    });

    this.quantivePlusEffectivenessFacade
      .getQuantivePlusObjectivesEffectiveness(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (effectivenessRes) => {
          this.$scope.effectivenessFlowId = effectivenessRes.flowId;

          this.$scope.drawerEffectivenessSection = {
            ...this.$scope.drawerEffectivenessSection,
            effectiveness: effectivenessRes.suggestions[0].effectiveness[0],
            hasError: false,
            isLoading: false,
          };

          const suggestionText = effectivenessRes.suggestions[0]?.effectiveness[0]?.suggested_objective_title;
          const suggestionId = effectivenessRes.suggestions[0]?.effectiveness[0]?.id;
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            isHidden: !suggestionText || (!this.$scope.allowEditGoalFields && !this.$scope.isFieldPartOfWorkflow("name")),
            suggestions: [
              {
                id: suggestionId,
                suggestionText,
              },
            ],
          };

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerEffectivenessSection = {
            ...this.$scope.drawerEffectivenessSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("effectiveness", err),
          };
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };

          this.$scope.$evalAsync();
        },
      });
  }

  private loadTitleSuggestionPartOfEffectiveness(
    goalData: Pick<IGoal, "ownerIds" | "name" | "description"> & { goalChildren: ContributorType[]; isRefresh: boolean; rejected: SuggestedEffectivenessTitle }
  ): void {
    if (!this.$scope.isSuggestionDrawerEffectivenessTabEnabled) return;

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
      hasError: false,
    };
    this.$scope.$evalAsync();
    const payload = this.generateQuantivePlusObjectivesEffectivenessPayload({
      ...goalData,
      refresh: goalData.isRefresh,
      accepted: {},
      rejected: goalData.rejected,
      entityId: this.$scope.goal.id,
      subEntityType: "effectivenessTitle",
    });

    this.quantivePlusEffectivenessFacade
      .getQuantivePlusObjectivesEffectiveness(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (effectivenessRes) => {
          const suggestionText = effectivenessRes.suggestions[0]?.effectiveness[0]?.suggested_objective_title;
          const suggestionId = effectivenessRes.suggestions[0]?.effectiveness[0]?.id;
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            isHidden: !suggestionText || (!this.$scope.allowEditGoalFields && !this.$scope.isFieldPartOfWorkflow("name")),
            suggestions: [
              {
                id: suggestionId,
                suggestionText,
              },
            ],
          };

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };
          this.$scope.$evalAsync();
        },
      });
  }

  private setAcceptedTitleSuggestion = (): void => {
    this.updateName(this.$scope.drawerTitleSection.suggestions[0].suggestionText);
  };

  private showLastAvailableTitleSuggestion = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      hasError: false,
      errorData: getPiErrorData("title", null),
    };
    this.$scope.$evalAsync();
  };

  private loadData = (): IPromise<void> => {
    this.goalDataLoading = true;
    this.$scope.indicators.loadingGoalProgress = true;

    this.checkMarketplaceSubmissionFeatureToggle();

    this.piFeatureVisibilityFacade
      .isGuidedOKRsCreationVisible$()
      .pipe(take(1))
      .subscribe((enabled) => (this.$scope.isGuidedOKRsCreationEnabled = enabled));

    return this.$q
      .all([
        this.getGoal(),
        this.getAllPolicies(),
        this.getReduxItems(),
        this.checkMandatoryOKRsAlignmentFeatureToggle(),
        this.checkSuggestionStateIndicatorFeatureToggle(),
        this.checkSuggestionDrawerOverviewTabFeatureToggle(),
        this.checkSuggestionDrawerEffectivenessTabFeatureToggle(),
        this.checkRelatedItemsFeatureToggle(),
        this.checkTagsFeatureToggle(),
        this.checkA14CloneAndMoveModalsFeatureToggle(),
      ])
      .then(() => {
        this.goalDataLoading = false;
        if (this.$scope.goal && this.$scope.goal.private && this.$scope.goal.obfuscated) {
          this.$scope.customPrivateError = {
            type: "private_okr",
            title: localize("this_okr_is_private"),
          };

          delete this.$scope.indicators.getGoal;
          return;
        }
        this.$scope.shouldSeeOnboardingExperiment = this.onboardingService.shouldSeeOnboardingExperiment();
        this.okrEvents.broadcastGoalsChanged("goalVisited", { goal: this.$scope.goal });
        this.setSessionToScope();
        this.setMethodologySettings();
        this.setCanChangeClosedStatus();
        this.adjustSessionDetails();
        this.getParentOkr();
        this.syncPersistedGoalFields();

        return this.$q.all([this.getSubGoalsIfNeeded(), this.getWorkflowActionsIfNeeded(), this.getCurrentUserAllowedSessionActions()]).then(() => {
          this.$scope.allowEditGoalFields =
            this.$scope.planningSession.status === "open" &&
            !this.$scope.goal.locked &&
            this.$rootScope.hasPermission("ManageGoals") &&
            this.isCustomPermissionAllowed("update");

          this.$scope.goalChildren = [...(this.$scope.goal.metrics || []), ...(this.$scope.subGoals || [])].sort((a, b) => a.orderId - b.orderId);
          delete this.$scope.indicators.getGoal;
          this.$scope.indicators.loadingGoalProgress = false;
          const metricsCount = this.$scope.goal.metrics?.length || 0;
          this.$scope.metricsLimitReached = metricsCount >= this.$scope.methodologySettings.metricsPerGoalLimit;
          this.setPeopleSelectorRequest();

          if (this.designScoreForUpdate) {
            this.updateGoalDesignScore(this.designScoreForUpdate);
            this.designScoreForUpdate = null;
          }

          this.setPiFeaturePackagingFlags();
          this.setSuggestionDrawerSectionsImproveSuggestionContext();
        });
      })
      .catch((error) => {
        this.$scope.indicators.getGoal = { error };
      });
  };

  private handleQuantivePlusDescriptionSuggestionsFetchingOnDataLoad = (): void => {
    if (this.shouldFetchQuantivePlusDescriptionSuggestion()) {
      this.getQuantivePlusDescriptionSuggestion();
    }
  };

  private handleQuantivePlusTagsSuggestionsFetchingOnDataLoad = (): void => {
    if (!this.isTagsInputOpen && this.shouldFetchQuantivePlusTagsSuggestion()) {
      this.getQuantivePlusTagsSuggestion();
    }
  };

  private handleQuantivePlusMetricsAndTasksSuggestionsFetchingOnDataLoad = (): void => {
    if (!this.$scope.suggestions && (this.shouldSuggestQuantivePlusMetrics() || this.shouldSuggestQuantivePlusTasks())) {
      this.getQuantivePlusSuggestions();
      this.setSuggestAnotherObjectiveTaskSubscription();
      this.setRefreshAllObjectiveTasksSubscription();
    }
  };

  private setSuggestAnotherObjectiveTaskSubscription = (): void => {
    this.quantivePlusAnotherSuggestionService
      .getObjectiveTaskSuggestionToReplace$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (
          args: {
            taskToReplace: IQuantivePlusTask;
            taskToReplaceIndex: number;
            flowName: ObjectiveFlowName;
          } | null
        ) => {
          if (args) {
            this.changeQuantivePlusSuggestion(
              {
                suggestionType: "task",
                suggestionToReplace: args.taskToReplace,
                suggestionToReplaceIndex: args.taskToReplaceIndex,
              },
              args.flowName
            );
          }
        }
      );
  };

  private setRefreshAllObjectiveTasksSubscription = (): void => {
    this.quantivePlusAnotherSuggestionService
      .getRefreshAllObjectiveTasksEvent$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((args: { isRefreshAllTasks: true } | null) => {
        if (args) {
          this.getQuantivePlusSuggestions();
        }
      });

    this.quantivePlusAnotherSuggestionService.setRefreshAllObjectiveTasksEvent(null);
  };

  private enrichSubgoals = (goals: IGoal[]): IGoal[] => {
    return goals.map((goal) => ({
      ...goal,
      color: this.progressColoring.getColorOrGeneric(goal.attainment),
      childType: "goal",
    }));
  };

  private enrichChildMetrics = (metrics: Metric[]): Metric[] => metrics.map((metric) => ({ ...metric, childType: "metric" }));

  private selectMultipleOwners = (ids: string[]): void => {
    if (ids.length === 0) {
      return;
    }

    this.$scope.goal.assignees = ids.map((id) => this.$scope.assigneesIdMap[id]);
    this.$scope.goal.ownerIds = ids;

    this.updateField(this.$scope.goal, ["ownerIds", "assignees"]).then(() => {
      this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren, ownerIds: ids });
    });
  };

  private handleClickOnHiddenOverflowArea = ($event: PointerEvent): void => {
    const eventTarget = $event.target as HTMLElement;

    if (eventTarget.classList.contains("scrollable-with-left-overflow")) {
      this.closeModal();
    }
  };

  private closeGoalDetailsModalOnClick = ($event: PointerEvent): void => {
    if ($event.type === "click" && $event.isTrusted === true) {
      this.closeModal();
    }
  };

  private closeGoalDetailsModalOnEnter = ($event: KeyboardEvent): void => {
    if ($event.key === "Enter") {
      this.$timeout(() => {
        this.closeModal();
      }, 200);
    }
  };

  private closeModal = (): void => {
    this.$scope.$dismiss();

    if (this.onboardingService.shouldSeeFirstOkrGuide()) {
      this.$rootScope.$broadcast("firstOkrCreated", this.$scope.planningSession);
    }
  };

  private adjustSessionDetails = () => {
    this.measureDueDateDifference();

    const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
    const sincePlanningSessionStart = dayjs().diff(this.$scope.planningSession.start) / 86400000;

    this.$scope.planningSession.timePassedPercentage = (sincePlanningSessionStart / totalDays) * 100;
  };

  private measureDueDateDifference = () => {
    const goal = this.$scope.goal;

    if (goal.metrics) {
      for (let i = 0; i < goal.metrics.length; i++) {
        if (goal.metrics[i].dueDate !== "0001-01-01T00:00:00Z") {
          const daysToComplete = dayjs(goal.metrics[i].dueDate).diff(this.$scope.planningSession.start) / 86400000;
          const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
          const percentOfTime = (daysToComplete / totalDays) * 100;
          const daysLeft = dayjs(goal.metrics[i].dueDate).diff(dayjs()) / 86400000;

          goal.metrics[i].percentTime = percentOfTime;
          goal.metrics[i].daysLeft = daysLeft;
        }

        if (goal.metrics[i].softDueDate !== "0001-01-01T00:00:00Z") {
          const daysToCompleteSoft = dayjs(goal.metrics[i].softDueDate).diff(this.$scope.planningSession.start) / 86400000;
          const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
          const percentOfTimeToSoft = (daysToCompleteSoft / totalDays) * 100;
          const daysLeft = dayjs(goal.metrics[i].softDueDate).diff(dayjs()) / 86400000;

          goal.metrics[i].percentTimeToSoftDeadline = percentOfTimeToSoft;
          goal.metrics[i].daysLeftToSoftDeadline = daysLeft;
        }
      }
    }
  };

  private confirmDeleteMetric = (metricId: string | { itemId: string }): void => {
    if (typeof metricId === "string") {
      this.metricId = metricId;
    } else if (typeof metricId === "object" && "itemId" in metricId) {
      this.metricId = metricId.itemId;
    }
    this.confirmDeleteService.confirmDeleteItem({ itemKey: "key_result", action: () => Promise.resolve(this.deleteMetric()) });
  };

  private deleteMetric = (): IPromise<void> => {
    return this.$rootScope.traceAction("delete_KR", () => {
      return this.metricService.deleteMetric(this.$scope.goal.id, this.metricId).then(() => {
        for (let i = 0; i < this.$scope.goal.metrics.length; i++) {
          if (this.$scope.goal.metrics[i].id === this.metricId) {
            this.okrEvents.broadcastGoalsChanged("metricDeleted", {
              goalId: this.$scope.goal.id,
              metricId: this.$scope.goal.metrics[i].id,
            });
            this.trackMetricMilestoneDelete(i);

            this.$scope.goal.metrics.splice(i, 1);
            break;
          }
        }

        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");

        this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.goal, goalChildren: this.$scope.goalChildren.filter((child) => child.id !== this.metricId) });

        if (this.$scope.isSuggestingKRsToOKRsEnabled) {
          const minKrsCountNotToShowKrsSuggestions = Math.min(this.$scope.methodologySettings.metricsPerGoalLimit, maxKrsSuggestions);

          if ([...this.$scope.goal.metrics, ...this.$scope.subGoals].length < minKrsCountNotToShowKrsSuggestions) {
            this.addQuantivePlusMetricSuggestion();
          }
        }
      });
    });
  };

  private trackMetricMilestoneDelete = (i: number): void => {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.MetricMilestone)
      .pipe(first())
      .subscribe((isFeatureEnabled) => {
        if (isFeatureEnabled) {
          this.metricService.trackMetricMilestone(MetricMilestoneEventType.KR_MILESTONES_DELETED, { metric: this.$scope.goal.metrics[i] });
        }
      });
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === "metricUpdated") {
      this.loadData().then(() => {
        this.setFocusToUpdateBtn(args.detail.metric.id);
      });
    }

    if (args.reason === "metricCreated") {
      this.onMetricCreated(args.detail.metric);
    }

    if (
      args.reason === "goalMoved" ||
      args.reason === "goalCreated" ||
      args.reason === "goalPatched" ||
      args.reason === "goalStatusChanged" ||
      args.reason === "goalChanged" ||
      args.reason === "metricLinked" ||
      args.reason === "metricUnlinked"
    ) {
      this.loadData();
    }
  };

  private setFocusToUpdateBtn(metricId: string): void {
    this.$timeout(() => {
      const metricUpdateBtnElement = document.getElementById(`update-metric-button-${metricId}`);
      metricUpdateBtnElement?.focus();
    }, 10);
  }

  private updateGoalDesignScore = (designScoreForUpdate: IDesignScoreForUpdate) => {
    if (this.$scope.goal && designScoreForUpdate.goalId === this.$scope.goal.id) {
      this.$scope.goal.designScore = designScoreForUpdate.designScore;

      designScoreForUpdate.designScore.policies.forEach((policyForUpdate: IGoalPolicy) => {
        this.$scope.goalPoliciesMap[policyForUpdate.policy] = policyForUpdate;
      });

      this.drawCircularProgressBar(this.$scope.goal.designScore.totalPoints);
    }
  };

  private drawCircularProgressBar = (progressValue: number) => {
    this.$timeout(() => {
      const barEl = document.getElementById("left-side-circular-progress-bar");
      if (barEl) {
        barEl.style.transform = `rotate(${progressValue * 3.6}deg)`;
      }
    });
  };

  private switchProgressCalculationCriteria = (newCriteria: AttainmentTypeString): void => {
    this.$scope.indicators.loadingGoalProgress = true;
    this.$scope.goal.attainmentTypeString = newCriteria;

    const includeMetrics = this.$state.current.name.includes(".tree.");
    // https://gtmhub.atlassian.net/browse/GVS-6795 - patchGoal method also broadcasts and loadData method is called twice
    this.goalService.patchGoal(this.$scope.goal.id, { attainmentTypeString: newCriteria }, { includeMetrics }).then(
      () => {
        this.$rootScope.$broadcast("goalsChanged");
        this.okrEvents.broadcastGoalsChanged("goalChanged");
      },
      (error) => {
        this.$scope.indicators.loadingGoalProgress = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private onTextareaClick = ($event): void => {
    if ($event.target.tagName.toLowerCase() === "a") {
      $event.stopPropagation();
      $event.preventDefault();

      window.open($event.target.href, "_blank").focus();
    }
  };

  private openCloneMetricModal = (metric: Metric): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openCloneKrModal(metric);
    } else {
      this.cloneOkrsModal.openForMetricClone(metric);
    }
  };

  private openDisableSyncModal = (metricId: string): void => {
    this.disableMetricSyncModal.open(metricId);
  };

  private openMoveMetricModal = (metric: Metric): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openMoveOkrModal(ResourceType.KeyResult, this.$scope.goal, metric);
    } else {
      this.goalModalsService.openModalForMove("metric", metric, this.$scope.goal);
    }
  };

  private openUpdateMetricModal = (metricId: string): void => {
    this.updateMetricModalService.openUpdateMetricModal({ metricId }).pipe(takeUntil(this.destroy$)).subscribe();
  };

  private changeSession = (sessionId?: string): void => {
    if (!this.$scope.allowEditGoalFields) {
      return;
    }

    if (this.$scope.goal.sessionId === sessionId) {
      return;
    }

    this.$scope.goal.sessionId = sessionId;
    this.$scope.filterBySessionId = this.filterBySessionId.bind(this);
    this.updateField(this.$scope.goal, ["sessionId", "parentId"]);
  };

  private addChildObjective = (itemId: string, itemType: ItemType, itemSessionId: string): void => {
    this.$state.go("._goalForm.form", {
      id: this.$scope.goal.id,
      parentId: itemId,
      parentType: itemType,
      externalSessionId: itemSessionId || this.$scope.goal.sessionId,
      flowName: PiTrackingFlowNameEnum.CreateObjectiveFormOkrViewDropdown,
    });
  };

  private updateGoalParent = (parent: IGoalParent) => {
    const newParentId = parent?.id ?? "";
    if (newParentId === this.$scope.goal.parentId) {
      return;
    }

    const partialGoal: Partial<IGoal> = {
      parentId: newParentId,
    };
    const includeMetrics = this.$state.current.name.includes(".tree.");

    this.goalService.patchGoal(this.$scope.goal.id, partialGoal, { includeMetrics }).then(
      () => {
        this.loadData();

        this.$rootScope.$broadcast("goalsChanged");
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private canUpdateMetric = (metric: Metric): boolean => {
    if (!metric) {
      return false;
    }

    const isMetricOverdue = metric.daysLeft < 0;

    return (
      !isMetricOverdue &&
      this.$rootScope.hasPermission("ManageGoals") &&
      !metric.sourceMetricId &&
      this.$scope.goal.closedStatus?.status !== "abandoned" &&
      (this.$scope?.sessionAllowedActions?.includes("update") ||
        isCurrentUserOwner(
          metric.ownerIds,
          this.currentUserService.getCurrentUserShadowRoles().map((role) => role.id)
        ))
    );
  };

  private isCustomPermissionAllowed = (permission: UserAction): boolean => {
    return this.$scope.goal.currentUserAllowedActions?.includes(permission);
  };

  private metricsContributeToGoalProgress = (): boolean => {
    if (!this.$scope.goal) {
      return false;
    }

    return (
      !this.$scope.goal.attainmentTypeString || this.$scope.goal.attainmentTypeString === "average_kr" || this.$scope.goal.attainmentTypeString === "average_children_kr"
    );
  };

  private goalUnlockedAndSessionOpen = (): boolean => {
    if (!this.$scope.goal || !this.$scope.planningSession) {
      return false;
    }

    return this.$scope.planningSession.status === "open" && !this.$scope.goal.locked;
  };

  public isFieldDisabled = (fieldName: string): boolean => {
    if (this.$scope.planningSession.workflow?.editableOkrFields?.includes(fieldName) || this.$scope.allowEditGoalFields) {
      return false;
    }

    return true;
  };

  private isFieldPartOfWorkflow(filedName: string): boolean {
    return this.$scope.planningSession.workflow?.editableOkrFields?.includes(filedName);
  }

  private filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.$scope.goal.sessionId, customFields);
  };

  private goalUnlockedUnabandonedSessionOpen = (): boolean => {
    return this.goalUnlockedAndSessionOpen() && this.$scope.goal.closedStatus?.status !== "abandoned";
  };

  private getInfoTooltipReason = (): DisallowedMetricCreationReason => {
    if (this.$scope.goal.closedStatus?.status === "abandoned") {
      return "abandoned";
    }
    if (this.$scope.planningSession.status === PlanningSessionStatus.INPROGRESS) {
      return "session-in-progress";
    }
    if (this.$scope.goal.locked) {
      return "approved";
    }
    if (this.$scope.metricsLimitReached) {
      return "metrics-limit-reached";
    }
  };

  private reorderGoalChild = (params: IDndDroppedParams<ContributorType>): IPromise<IOnDndDroppedCbResponse<IGoalChildReorderResponse>> => {
    const { items, item, oldIndex, newIndex } = params;

    const movedItem: IGoalChildReorderPayload = {
      parentId: this.$scope.goal.id,
      moved: {
        id: item.id,
        type: item.childType,
      },
    };

    const firstElementIndex = 0;
    const lastElementIndex = items.length - 1;

    switch (newIndex) {
      case firstElementIndex: {
        movedItem.next = {
          id: items[firstElementIndex].id,
          type: items[firstElementIndex].childType,
        };
        break;
      }
      case lastElementIndex: {
        movedItem.previous = {
          id: items[lastElementIndex].id,
          type: items[lastElementIndex].childType,
        };
        break;
      }
      default: {
        let indexPrevious: number;
        let indexNext: number;

        if (oldIndex < newIndex) {
          indexPrevious = newIndex;
          indexNext = newIndex + 1;
        } else {
          indexPrevious = newIndex - 1;
          indexNext = newIndex;
        }

        movedItem.previous = {
          id: items[indexPrevious].id,
          type: items[indexPrevious].childType,
        };
        movedItem.next = {
          id: items[indexNext].id,
          type: items[indexNext].childType,
        };
      }
    }

    return this.goalService.reorderGoalChild(movedItem).then(
      (data) => {
        return this.$q.resolve({ shouldProceed: true, data });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.resolve({ shouldProceed: false });
      }
    );
  };

  private cdkReorderGoalChild = (params: IDndDroppedParams<ContributorType>): IPromise<IOnDndDroppedCbResponse<IGoalChildReorderResponse>> => {
    const { items, item, newIndex } = params;
    const movedItem: IGoalChildReorderPayload = {
      parentId: this.$scope.goal.id,
      moved: {
        id: item.id,
        type: item.childType,
      },
    };

    const firstElementIndex = 0;
    const lastElementIndex = items.length - 1;

    const indexPrevious = newIndex - 1;
    const indexNext = newIndex + 1;

    if (newIndex === firstElementIndex) {
      movedItem.next = {
        id: items[firstElementIndex + 1].id,
        type: items[firstElementIndex + 1].childType,
      };
    } else if (newIndex === lastElementIndex) {
      movedItem.previous = {
        id: items[lastElementIndex - 1].id,
        type: items[lastElementIndex - 1].childType,
      };
    } else {
      movedItem.previous = {
        id: items[indexPrevious].id,
        type: items[indexPrevious].childType,
      };
      movedItem.next = {
        id: items[indexNext].id,
        type: items[indexNext].childType,
      };
    }

    return this.goalService.reorderGoalChild(movedItem).then(
      (data) => {
        return this.$q.resolve({ shouldProceed: true, data });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.resolve({ shouldProceed: false });
      }
    );
  };

  private onReorderSuccess = (params: IDndInsertedParams<IGoalChildReorderResponse, ContributorType>): void => {
    const goalChildToBeUpdated = this.$scope.goalChildren.find((item) => item.id === params.item.id);
    if (goalChildToBeUpdated) {
      goalChildToBeUpdated.orderId = params.data.orderId;
    }

    if (params.item.childType === "metric") {
      const metricToBeUpdated = this.$scope.goal.metrics.find((item) => item.id === params.item.id);
      if (metricToBeUpdated) {
        metricToBeUpdated.orderId = params.data.orderId;
      }
    }

    if (params.item.childType === "goal") {
      const goalToBeUpdated = this.$scope.subGoals.find((item) => item.id === params.item.id);
      if (goalToBeUpdated) {
        goalToBeUpdated.orderId = params.data.orderId;
      }
    }
  };

  private copyItemUrlToClipboard = (itemId: string, type: ItemType): void => {
    const copyToClipboardOptions = { container: getCopyURLNotificationEl(), duration: 3000 };
    const url = this.getShareableUrl(itemId, type);

    copyToClipboard(url, copyToClipboardOptions);
  };

  private copyItemUrlFromContributors = (input: { itemId: string; type: ItemType }): void => {
    this.copyItemUrlToClipboard(input.itemId, input.type);
  };

  private getShareableUrl = (itemId: string, type: ItemType): string => {
    const states = {
      alignmentTree: "gtmhub.goals.alignmentSidebar.tree",
      nested: "gtmhub.goals.alignmentSidebar.nested",
      metric: {
        grid: "gtmhub.goals.all._metric.metric",
        nested: "gtmhub.goals.alignmentSidebar.nested._goalSidebar.goal._metric.metric",
      },
      goal: {
        grid: "gtmhub.goals.all._goalSidebar.goal",
        nested: "gtmhub.goals.alignmentSidebar.nested._goalSidebar.goal",
      },
    };

    if (this.$state.current.name.includes(states.alignmentTree)) {
      return this.$state.href(
        states.alignmentTree,
        { ...this.$stateParams, sharedId: itemId, planningSessionId: this.$scope.goal.sessionId, sharedParentId: itemId },
        { absolute: true }
      );
    }

    if (type === "goal") {
      if (this.$state.current.name.includes(states.nested)) {
        return this.$state.href(states.goal.nested, { ...this.$stateParams, id: itemId, sharedGoal: true }, { absolute: true });
      }
      return this.$state.href(
        states.goal.grid,
        { ...this.$stateParams, id: itemId, planningSessionId: this.$scope.goal.sessionId, sharedGoal: true },
        { absolute: true }
      );
    }

    if (type === "metric") {
      if (this.$state.current.name.includes(states.nested)) {
        return this.$state.href(states.metric.nested, { ...this.$stateParams, metricId: itemId, sharedMetric: true }, { absolute: true });
      }
      return this.$state.href(
        states.metric.grid,
        { ...this.$stateParams, metricId: itemId, planningSessionId: this.$scope.goal.sessionId, sharedMetric: true },
        { absolute: true }
      );
    }
  };

  private setMethodologySettings = (): void => {
    this.$scope.methodologySettings = {
      ...this.okrMethodologyFacade.getMethodologySettingsUIForGoalView(this.$scope.goal, this.$scope.planningSession),
      isProgressAggregationDisabled: this.okrMethodologyFacade.isProgressAggregationDisabled(this.$scope.planningSession?.settings),
      metricsPerGoalLimit: this.okrMethodologyFacade.getLimitSetting("metric", this.$scope.planningSession?.settings),
    };

    this.$scope.sharedOkrsMethodologySettings = {
      areMultipleObjectiveOwnersDisabled: this.okrMethodologyFacade.areMultipleOwnersDisabled("goal", this.$scope.planningSession?.settings),
      areMultipleKrOwnersDisabled: this.okrMethodologyFacade.areMultipleOwnersDisabled("metric", this.$scope.planningSession?.settings),
    };
  };

  public getNavItem = (): INavItem => {
    if (!this.$scope.goal) return undefined;
    return this.favoritesManager.getFavoriteItem({ id: this.$scope.goal.id }) || navItemsFactory.buildGoalNavItem(this.$scope.goal);
  };

  public goalIsFavorite = (): boolean => {
    if (!this.$scope.goal) return false;
    return this.favoritesManager.entityIsFavorite({ id: this.$scope.goal.id });
  };

  private setPeopleSelectorRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      ...(this.$scope.methodologySettings.goalsPerOwner.sessionId ? { goalsLimitSetting: this.$scope.methodologySettings.goalsPerOwner } : {}),
      type: this.$scope.methodologySettings.assignGoalPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  private setPiFeaturePackagingFlags(): void {
    const quantivePlusSuggestionsEnabled = combineLatest([
      this.piFeatureVisibilityFacade.isSuggestingTasksToOKRsVisible$(),
      this.piFeatureVisibilityFacade.isSuggestingKRsToOKRsVisible$(),
      this.piFeatureVisibilityFacade.isSuggestingDescriptionToOKRsVisible$(),
      this.piFeatureVisibilityFacade.isSuggestingTagsToOKRsVisible$(),
    ]);

    quantivePlusSuggestionsEnabled.pipe(takeUntil(this.destroy$)).subscribe(([areTasksEnabled, areKrsEnabled, isDescriptionEnabled, areTagsEnabled]) => {
      this.$scope.isSuggestingKRsToOKRsEnabled = areKrsEnabled;
      this.$scope.isSuggestingTasksToOKRsEnabled = areTasksEnabled;
      this.$scope.isSuggestingDescriptionToOKRsEnabled = isDescriptionEnabled;
      this.$scope.isSuggestingTagsToOKRsEnabled = areTagsEnabled;

      if (areKrsEnabled) {
        this.piSuggestionSharedGoalStateService.setShouldSuggestQuantivePlusMetrics(this.shouldSuggestQuantivePlusMetrics());
      }

      if (areTasksEnabled) {
        this.piSuggestionSharedGoalStateService.setShouldSuggestQuantivePlusTasks(hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageTasks"));
      }

      if (isDescriptionEnabled) {
        this.handleQuantivePlusDescriptionSuggestionsFetchingOnDataLoad();

        this.piSuggestionSharedGoalStateService.setShouldSuggestQuantivePlusDescription(this.$scope.allowEditGoalFields);
      }

      if (areTagsEnabled) {
        this.handleQuantivePlusTagsSuggestionsFetchingOnDataLoad();

        this.piSuggestionSharedGoalStateService.setShouldSuggestQuantivePlusTags(this.$scope.allowEditGoalFields && this.$scope.isTagsModuleEnabled);
      }

      if (areKrsEnabled || areTasksEnabled) {
        this.handleQuantivePlusMetricsAndTasksSuggestionsFetchingOnDataLoad();
      }
    });
  }

  private setDefaultSharedOkrsMethodologySettings(): void {
    this.$scope.sharedOkrsMethodologySettings = {
      areMultipleObjectiveOwnersDisabled: false,
      areMultipleKrOwnersDisabled: false,
    };
  }
}
